import Debug from '../utils/Debug.js';
import GridPathfinder from './GridPathfinder.js';

/**
 * Hierarchical A* pathfinding implementation.
 * Uses two-tier approach:
 * - High-level: A* on navigation graph (surface-to-surface)
 * - Low-level: Grid-based A* within each surface
 */
export default class PathFinder {
  /**
   * Create a pathfinder
   * @param {NavGraph} navGraph - The navigation graph to search
   * @param {CharacterCollisionSystem} collisionSystem - For line-of-sight checks
   */
  constructor(navGraph, collisionSystem = null) {
    this.navGraph = navGraph;
    this.collisionSystem = collisionSystem;
  }

  /**
   * Set a new navigation graph
   * @param {NavGraph} navGraph - The new navigation graph
   */
  setNavGraph(navGraph) {
    this.navGraph = navGraph;
  }

  /**
   * Set the collision system for line-of-sight checks
   * @param {CharacterCollisionSystem} collisionSystem
   */
  setCollisionSystem(collisionSystem) {
    this.collisionSystem = collisionSystem;
  }

  /**
   * Find a path between two positions
   * @param {Object} start - Start position {x, y, z}
   * @param {Object} end - End position {x, y, z}
   * @returns {Array|null} Array of waypoints [{x, y, z, type?}, ...] or null if no path
   */
  findPath(start, end) {
    if (!this.navGraph) {
      Debug.warn('PathFinder', 'No navigation graph set');
      return null;
    }

    // Find nearest nodes to start and end positions
    const startNode = this.navGraph.findNearestNode(start.x, start.y, start.z);
    const endNode = this.navGraph.findNearestNode(end.x, end.y, end.z);

    if (!startNode || !endNode) {
      Debug.warn('PathFinder', 'Could not find start or end node');
      return null;
    }

    let path;

    // Try merged-plane pathfinding for same-Y surfaces.
    // This treats all coplanar floor surfaces as one unified grid, producing
    // direct paths that naturally avoid obstacles (pillars) without forcing
    // edge-to-edge transitions between individual surfaces.
    const samePlane = Math.abs(startNode.y - endNode.y) < 0.3;
    if (samePlane && this.collisionSystem) {
      path = this._findPathOnSamePlane(startNode, endNode, start, end);
      if (path && path.length > 0) {
        // Verify path reaches near destination (A* may time out on very large grids)
        const last = path[path.length - 1];
        const distToEnd = Math.sqrt((last.x - end.x) ** 2 + (last.z - end.z) ** 2);
        if (distToEnd < 3.0) {
          // Merged-plane paths have correct edge margins baked in — skip _enforceEdgeMargins
          path = this._validateAndFixPath(path, start, end);
          return path;
        }
        path = null; // Too far from destination — fall back to hierarchical
      }
    }

    // Fall back to hierarchical approach
    if (startNode === endNode) {
      path = this._findPathWithinSurface(startNode, start, end);
      if (!path || path.length === 0) {
        path = this._findDetourPath(startNode, start, end);
      }
    } else {
      path = this._findPathAcrossSurfaces(startNode, endNode, start, end);
    }

    // Validate and fix path issues
    if (path && path.length > 0) {
      Debug.log('PathFinder', 'BEFORE _validateAndFixPath:', path.length, 'waypoints');
      for (let i = 0; i < Math.min(path.length, 5); i++) {
        const wp = path[i];
        Debug.log('PathFinder', '  PRE-VALIDATE [' + i + '] x=' + wp.x.toFixed(2) + ' y=' + wp.y.toFixed(2) + ' z=' + wp.z.toFixed(2));
      }

      path = this._validateAndFixPath(path, start, end);

      Debug.log('PathFinder', 'AFTER _validateAndFixPath:', path.length, 'waypoints');
      for (let i = 0; i < Math.min(path.length, 5); i++) {
        const wp = path[i];
        Debug.log('PathFinder', '  POST-VALIDATE [' + i + '] x=' + wp.x.toFixed(2) + ' y=' + wp.y.toFixed(2) + ' z=' + wp.z.toFixed(2));
      }

      // Enforce edge margins to prevent falling off surfaces
      path = this._enforceEdgeMargins(path);

      Debug.log('PathFinder', 'AFTER _enforceEdgeMargins:', path.length, 'waypoints');
      for (let i = 0; i < Math.min(path.length, 5); i++) {
        const wp = path[i];
        Debug.log('PathFinder', '  POST-MARGINS [' + i + '] x=' + wp.x.toFixed(2) + ' y=' + wp.y.toFixed(2) + ' z=' + wp.z.toFixed(2));
      }
    }

    return path;
  }

  /**
   * Validate path and fix issues like backtracking
   * @private
   */
  _validateAndFixPath(waypoints, start, end) {
    if (waypoints.length <= 2) return waypoints;

    // Check for backtracking: if any waypoint moves away from the destination
    // when a direct path would be shorter
    const result = [waypoints[0]];

    for (let i = 1; i < waypoints.length; i++) {
      const prev = result[result.length - 1];
      const current = waypoints[i];

      // Check if this waypoint causes backtracking
      if (result.length > 1 && Math.abs(prev.y - current.y) < 0.1) {
        const prevPrev = result[result.length - 2];

        // Direction from prevPrev to prev
        const dir1X = prev.x - prevPrev.x;
        const dir1Z = prev.z - prevPrev.z;

        // Direction from prev to current
        const dir2X = current.x - prev.x;
        const dir2Z = current.z - prev.z;

        // Dot product - negative means backtracking
        const dot = dir1X * dir2X + dir1Z * dir2Z;

        if (dot < -0.1) {
          // Backtracking detected - check if we can skip to current directly
          if (this._hasLineOfSight(prevPrev, current, prev.y)) {
            // Remove the backtracking waypoint and go direct
            result.pop();
          }
        }
      }

      result.push(current);
    }

    // Final check: ensure path actually ends closer to destination
    if (result.length > 0) {
      const lastWp = result[result.length - 1];
      const distToEnd = Math.sqrt(
        Math.pow(lastWp.x - end.x, 2) +
        Math.pow(lastWp.z - end.z, 2)
      );

      // If last waypoint is far from destination, add destination as final waypoint
      // BUT only if the destination is on a valid walkable surface (prevents off-edge waypoints)
      if (distToEnd > 0.5) {
        const destNode = this.navGraph.findNodeAtPoint(end.x, lastWp.y, end.z, 1.0);
        if (destNode) {
          // Destination is on a walkable surface — clamp to safe bounds
          const MARGIN = 0.5;
          const clampedX = Math.max(destNode.minX + MARGIN, Math.min(destNode.maxX - MARGIN, end.x));
          const clampedZ = Math.max(destNode.minZ + MARGIN, Math.min(destNode.maxZ - MARGIN, end.z));
          result.push({
            x: clampedX,
            y: destNode.y,
            z: clampedZ,
            type: 'walk'
          });
        }
        // If destination is off any surface, don't add it — end at last valid waypoint
      }
    }

    return result;
  }

  /**
   * Enforce edge margins on waypoints to prevent falling off surfaces
   * Walk waypoints should be at least EDGE_MARGIN inside the surface bounds
   * When approaching a transition, stay within the overlap region with the next surface
   * Transition waypoints (jump/fall) are allowed at edges
   * @private
   */
  _enforceEdgeMargins(waypoints) {
    if (!waypoints || waypoints.length === 0) return waypoints;

    const EDGE_MARGIN = 0.6; // Stay this far from surface edges (increased for safety)
    const TRANSITION_EDGE_MARGIN = 0.75; // Extra margin near transitions to prevent edge falls
    const NARROW_SURFACE_THRESHOLD = 6.0; // Surfaces narrower than this get extra centering
    const result = [];

    for (let i = 0; i < waypoints.length; i++) {
      const wp = waypoints[i];

      // Transition waypoints need to be at edges, so don't adjust them
      if (wp.type === 'jump' || wp.type === 'fall') {
        result.push(wp);
        continue;
      }

      // Find the surface node for this waypoint
      const node = this.navGraph.findNodeAtPoint(wp.x, wp.y, wp.z, 1.0);
      if (!node) {
        Debug.log('PathFinder', '_enforceEdgeMargins: no node found for wp[' + i + '] at',
                  wp.x.toFixed(2), wp.y.toFixed(2), wp.z.toFixed(2));
        result.push(wp);
        continue;
      }
      Debug.log('PathFinder', '_enforceEdgeMargins: wp[' + i + '] at (' + wp.x.toFixed(2) + ', ' + wp.y.toFixed(2) + ', ' + wp.z.toFixed(2) + ')',
                'found node y=' + node.y.toFixed(1),
                'bounds x[' + node.minX.toFixed(1) + ', ' + node.maxX.toFixed(1) + ']',
                'z[' + node.minZ.toFixed(1) + ', ' + node.maxZ.toFixed(1) + ']');

      // Check if there's a transition coming up
      let nextTransitionNode = null;
      let waypointsUntilTransition = Infinity;
      for (let j = i + 1; j < waypoints.length; j++) {
        const nextWp = waypoints[j];
        if (nextWp.type === 'jump' || nextWp.type === 'fall' || Math.abs(nextWp.y - wp.y) > 0.5) {
          // Found a height change - find the destination node
          nextTransitionNode = this.navGraph.findNodeAtPoint(nextWp.x, nextWp.y, nextWp.z, 1.0);
          waypointsUntilTransition = j - i;
          break;
        }
      }

      // Use larger margin when near transitions to prevent edge falls during staircases
      const effectiveMargin = (waypointsUntilTransition <= 2) ? TRANSITION_EDGE_MARGIN : EDGE_MARGIN;

      // Start with surface bounds
      let minX = node.minX + effectiveMargin;
      let maxX = node.maxX - effectiveMargin;
      let minZ = node.minZ + effectiveMargin;
      let maxZ = node.maxZ - effectiveMargin;

      // If transitioning to another surface, constrain to overlap region
      // BUT only if this waypoint is immediately before the transition (within 1 step)
      // This preserves the path around obstacles while ensuring proper approach to transitions
      if (nextTransitionNode && nextTransitionNode !== node && waypointsUntilTransition <= 1) {
        // Calculate overlap region between current and next surface (use larger margin for safety)
        const overlapMinX = Math.max(node.minX, nextTransitionNode.minX) + TRANSITION_EDGE_MARGIN;
        const overlapMaxX = Math.min(node.maxX, nextTransitionNode.maxX) - TRANSITION_EDGE_MARGIN;
        const overlapMinZ = Math.max(node.minZ, nextTransitionNode.minZ) + TRANSITION_EDGE_MARGIN;
        const overlapMaxZ = Math.min(node.maxZ, nextTransitionNode.maxZ) - TRANSITION_EDGE_MARGIN;

        // Use the more restrictive bounds (intersection of surface bounds and overlap)
        if (overlapMinX < overlapMaxX && overlapMinZ < overlapMaxZ) {
          minX = Math.max(minX, overlapMinX);
          maxX = Math.min(maxX, overlapMaxX);
          minZ = Math.max(minZ, overlapMinZ);
          maxZ = Math.min(maxZ, overlapMaxZ);
        }
      }

      // Ensure we have valid bounds
      if (minX >= maxX) {
        minX = node.minX + effectiveMargin;
        maxX = node.maxX - effectiveMargin;
      }
      if (minZ >= maxZ) {
        minZ = node.minZ + effectiveMargin;
        maxZ = node.maxZ - effectiveMargin;
      }

      // For narrow surfaces, force waypoints toward the center to prevent edge falls
      const surfaceWidth = node.maxX - node.minX;
      const surfaceDepth = node.maxZ - node.minZ;

      if (surfaceWidth < NARROW_SURFACE_THRESHOLD) {
        // Center the X bounds for narrow width
        const centerX = (node.minX + node.maxX) / 2;
        const halfSafeWidth = Math.max(0.5, (surfaceWidth - effectiveMargin * 2) / 2);
        minX = centerX - halfSafeWidth * 0.5; // Tighter bounds for narrow surfaces
        maxX = centerX + halfSafeWidth * 0.5;
      }

      if (surfaceDepth < NARROW_SURFACE_THRESHOLD) {
        // Center the Z bounds for narrow depth
        const centerZ = (node.minZ + node.maxZ) / 2;
        const halfSafeDepth = Math.max(0.5, (surfaceDepth - effectiveMargin * 2) / 2);
        minZ = centerZ - halfSafeDepth * 0.5; // Tighter bounds for narrow surfaces
        maxZ = centerZ + halfSafeDepth * 0.5;
      }

      // Check if waypoint is already outside the surface bounds
      // If so, the path smoother placed it there intentionally (for obstacle avoidance)
      // and we should NOT clamp it back inside
      const outsideX = wp.x < node.minX || wp.x > node.maxX;
      const outsideZ = wp.z < node.minZ || wp.z > node.maxZ;

      if (outsideX || outsideZ) {
        Debug.log('PathFinder', '_enforceEdgeMargins: wp[' + i + '] is OUTSIDE surface bounds, skipping clamp',
                  'wp=(' + wp.x.toFixed(2) + ', ' + wp.z.toFixed(2) + ')',
                  'node bounds x[' + node.minX.toFixed(1) + ', ' + node.maxX.toFixed(1) + ']',
                  'z[' + node.minZ.toFixed(1) + ', ' + node.maxZ.toFixed(1) + ']');
        result.push(wp);
        continue;
      }

      // Clamp waypoint to safe bounds (only for waypoints inside the surface)
      const clampedX = Math.max(minX, Math.min(maxX, wp.x));
      const clampedZ = Math.max(minZ, Math.min(maxZ, wp.z));

      // Only adjust if significantly different (avoid micro-adjustments)
      if (Math.abs(clampedX - wp.x) > 0.01 || Math.abs(clampedZ - wp.z) > 0.01) {
        Debug.log('PathFinder', '_enforceEdgeMargins CLAMPING wp[' + i + ']',
                  'from (' + wp.x.toFixed(2) + ', ' + wp.z.toFixed(2) + ')',
                  'to (' + clampedX.toFixed(2) + ', ' + clampedZ.toFixed(2) + ')',
                  'bounds: x[' + minX.toFixed(2) + ', ' + maxX.toFixed(2) + ']',
                  'z[' + minZ.toFixed(2) + ', ' + maxZ.toFixed(2) + ']',
                  'node:', node ? (node.minX.toFixed(1) + '-' + node.maxX.toFixed(1)) : 'null');
        result.push({
          x: clampedX,
          y: wp.y,
          z: clampedZ,
          type: wp.type
        });
      } else {
        result.push(wp);
      }
    }

    return result;
  }

  /**
   * Find path within a single surface using grid pathfinding
   * @private
   */
  _findPathWithinSurface(node, start, end) {
    Debug.log('PathFinder', '_findPathWithinSurface:',
              'node.y=' + node.y.toFixed(1),
              'start=(' + start.x.toFixed(1) + ',' + start.z.toFixed(1) + ')',
              'end=(' + end.x.toFixed(1) + ',' + end.z.toFixed(1) + ')');

    if (!node.hasGrid()) {
      // Fallback: direct path if no grid
      Debug.log('PathFinder', '_findPathWithinSurface: no grid, returning direct path');
      return [{ x: end.x, y: node.y, z: end.z, type: 'walk' }];
    }

    const gridPath = node.gridPathfinder.findPathWorld(
      { x: start.x, z: start.z },
      { x: end.x, z: end.z }
    );

    Debug.log('PathFinder', '_findPathWithinSurface: gridPath has', gridPath ? gridPath.length : 0, 'points');
    if (gridPath && gridPath.length > 0) {
      for (let i = 0; i < Math.min(gridPath.length, 5); i++) {
        const p = gridPath[i];
        Debug.log('PathFinder', '  GRID [' + i + '] x=' + p.x.toFixed(2) + ' z=' + p.z.toFixed(2));
      }
    }

    if (!gridPath || gridPath.length === 0) {
      // No grid path found — obstacle blocks all routes within this surface.
      // Return null to trigger detour through neighboring surfaces.
      Debug.log('PathFinder', '_findPathWithinSurface: no grid path, returning null for detour');
      return null;
    }

    // Convert grid path to waypoints with type annotation
    const waypoints = gridPath.map(pos => ({
      x: pos.x,
      y: node.y,
      z: pos.z,
      type: 'walk'
    }));

    // Smooth the path using line-of-sight checks
    const smoothed = this._smoothPath(waypoints, node.y);
    Debug.log('PathFinder', '_findPathWithinSurface: smoothed path has', smoothed.length, 'points');
    for (let i = 0; i < Math.min(smoothed.length, 5); i++) {
      const p = smoothed[i];
      Debug.log('PathFinder', '  SMOOTHED [' + i + '] x=' + p.x.toFixed(2) + ' z=' + p.z.toFixed(2));
    }
    return smoothed;
  }

  /**
   * Find a detour path when start and end are on the same surface but
   * within-surface pathfinding failed (obstacle blocks all routes).
   * Routes through a neighboring surface: startNode → neighbor → startNode
   * @param {NavNode} node - The surface both start and end are on
   * @param {Object} start - Start position {x, y, z}
   * @param {Object} end - End position {x, y, z}
   * @returns {Array|null} Waypoints or null
   * @private
   */
  _findDetourPath(node, start, end) {
    // Try each walk neighbor as a detour surface
    const walkNeighbors = node.neighbors
      .filter(n => Math.abs(n.node.y - node.y) <= 1.0) // walk-height neighbors only
      .sort((a, b) => {
        // Prefer neighbors that are between start and end in the blocked axis
        const aMidX = (a.node.minX + a.node.maxX) / 2;
        const aMidZ = (a.node.minZ + a.node.maxZ) / 2;
        const bMidX = (b.node.minX + b.node.maxX) / 2;
        const bMidZ = (b.node.minZ + b.node.maxZ) / 2;
        const midX = (start.x + end.x) / 2;
        const midZ = (start.z + end.z) / 2;
        const aDist = Math.abs(aMidX - midX) + Math.abs(aMidZ - midZ);
        const bDist = Math.abs(bMidX - midX) + Math.abs(bMidZ - midZ);
        return aDist - bDist;
      });

    for (const { node: neighbor } of walkNeighbors) {
      // Find transition points: start-side exit and end-side entry
      const exitPoint = this._findClosestEdgePoint(node, neighbor, start);
      const entryPoint = this._findClosestEdgePoint(node, neighbor, end);
      if (!exitPoint || !entryPoint) continue;

      // Try grid path: start → exitPoint (on node)
      const pathToExit = node.hasGrid()
        ? node.gridPathfinder.findPathWorld({ x: start.x, z: start.z }, { x: exitPoint.x, z: exitPoint.z })
        : null;

      // Try grid path: entryPoint → end (on node)
      const pathFromEntry = node.hasGrid()
        ? node.gridPathfinder.findPathWorld({ x: entryPoint.x, z: entryPoint.z }, { x: end.x, z: end.z })
        : null;

      if (!pathToExit && !pathFromEntry) continue;

      // Build the detour path
      const fullPath = [];

      // Segment 1: start → exit edge
      if (pathToExit) {
        for (const p of pathToExit) {
          fullPath.push({ x: p.x, y: node.y, z: p.z, type: 'walk' });
        }
      } else {
        fullPath.push({ x: exitPoint.x, y: node.y, z: exitPoint.z, type: 'walk' });
      }

      // Segment 2: cross through neighbor — find a path through the neighbor surface
      const neighborMidX = Math.max(neighbor.minX + 0.5, Math.min(neighbor.maxX - 0.5,
        (exitPoint.x + entryPoint.x) / 2));
      const neighborMidZ = Math.max(neighbor.minZ + 0.5, Math.min(neighbor.maxZ - 0.5,
        (exitPoint.z + entryPoint.z) / 2));

      if (neighbor.hasGrid()) {
        const neighborPath = neighbor.gridPathfinder.findPathWorld(
          { x: exitPoint.x, z: exitPoint.z },
          { x: entryPoint.x, z: entryPoint.z }
        );
        if (neighborPath) {
          for (const p of neighborPath) {
            fullPath.push({ x: p.x, y: neighbor.y, z: p.z, type: 'walk' });
          }
        } else {
          // Can't path through this neighbor
          continue;
        }
      } else {
        fullPath.push({ x: neighborMidX, y: neighbor.y, z: neighborMidZ, type: 'walk' });
      }

      // Segment 3: entry edge → end
      if (pathFromEntry) {
        for (const p of pathFromEntry) {
          fullPath.push({ x: p.x, y: node.y, z: p.z, type: 'walk' });
        }
      } else {
        fullPath.push({ x: end.x, y: node.y, z: end.z, type: 'walk' });
      }

      if (fullPath.length > 0) {
        Debug.log('PathFinder', '_findDetourPath: found detour via neighbor',
          neighbor.cuboidKey, 'with', fullPath.length, 'waypoints');
        return this._smoothPathWithHeightChanges(fullPath);
      }
    }

    Debug.warn('PathFinder', '_findDetourPath: no detour found through any neighbor');
    return null;
  }

  /**
   * Find the closest edge point between two adjacent nodes to a reference position.
   * @param {NavNode} from - Source node
   * @param {NavNode} to - Target neighbor node
   * @param {Object} ref - Reference position {x, z} to find closest edge point to
   * @returns {{x: number, z: number}|null}
   * @private
   */
  _findClosestEdgePoint(from, to, ref) {
    const MARGIN = 0.3;
    // Find the shared edge between the two nodes
    const overlapMinX = Math.max(from.minX, to.minX);
    const overlapMaxX = Math.min(from.maxX, to.maxX);
    const overlapMinZ = Math.max(from.minZ, to.minZ);
    const overlapMaxZ = Math.min(from.maxZ, to.maxZ);

    if (overlapMinX < overlapMaxX && overlapMinZ < overlapMaxZ) {
      // Surfaces overlap in XZ — find a point in the overlap
      return {
        x: Math.max(overlapMinX + MARGIN, Math.min(overlapMaxX - MARGIN, ref.x)),
        z: Math.max(overlapMinZ + MARGIN, Math.min(overlapMaxZ - MARGIN, ref.z))
      };
    }

    // Adjacent (touching edge)
    const TOLERANCE = 0.5;
    if (Math.abs(from.maxX - to.minX) <= TOLERANCE || Math.abs(from.minX - to.maxX) <= TOLERANCE) {
      // Shared X edge
      const edgeX = Math.abs(from.maxX - to.minX) <= TOLERANCE ? from.maxX : from.minX;
      const zMin = Math.max(from.minZ, to.minZ) + MARGIN;
      const zMax = Math.min(from.maxZ, to.maxZ) - MARGIN;
      if (zMin > zMax) return null;
      return { x: edgeX, z: Math.max(zMin, Math.min(zMax, ref.z)) };
    }
    if (Math.abs(from.maxZ - to.minZ) <= TOLERANCE || Math.abs(from.minZ - to.maxZ) <= TOLERANCE) {
      // Shared Z edge
      const edgeZ = Math.abs(from.maxZ - to.minZ) <= TOLERANCE ? from.maxZ : from.minZ;
      const xMin = Math.max(from.minX, to.minX) + MARGIN;
      const xMax = Math.min(from.maxX, to.maxX) - MARGIN;
      if (xMin > xMax) return null;
      return { x: Math.max(xMin, Math.min(xMax, ref.x)), z: edgeZ };
    }

    return null;
  }

  // ===== MERGED PLANE PATHFINDING =====
  // Treats all coplanar (same-Y) surfaces as one unified grid.
  // Eliminates edge-to-edge transitions and handles obstacle avoidance naturally.

  /**
   * Find a path on a unified grid spanning all same-Y surfaces.
   * @param {NavNode} startNode
   * @param {NavNode} endNode
   * @param {Object} start - {x, y, z}
   * @param {Object} end - {x, y, z}
   * @returns {Array|null} Waypoints or null
   * @private
   */
  _findPathOnSamePlane(startNode, endNode, start, end) {
    const surfaceY = startNode.y;

    // Collect all same-Y nodes that might be relevant
    const nodes = this._collectSamePlaneNodes(startNode, endNode, surfaceY);
    if (!nodes || nodes.length === 0) return null;

    // Build merged grid
    const mergedGrid = this._buildMergedPlaneGrid(nodes, surfaceY);
    if (!mergedGrid) return null;

    // Run A* on merged grid
    const pathfinder = new GridPathfinder(mergedGrid);
    const gridPath = pathfinder.findPathWorld(
      { x: start.x, z: start.z },
      { x: end.x, z: end.z }
    );
    if (!gridPath) return null;

    // Convert to waypoints
    const waypoints = gridPath.map(p => ({
      x: p.x, y: surfaceY, z: p.z, type: 'walk'
    }));

    // Smooth the path using line-of-sight checks
    return this._smoothPath(waypoints, surfaceY);
  }

  /**
   * Collect relevant same-Y NavNodes for merged-plane pathfinding.
   * Includes the A* path nodes plus one ring of same-Y walk neighbors.
   * @private
   */
  _collectSamePlaneNodes(startNode, endNode, surfaceY) {
    const STEP_HEIGHT = 0.3;
    const nodeSet = new Set();

    if (startNode === endNode) {
      nodeSet.add(startNode);
    } else {
      // Run A* to get surface sequence
      const nodePath = this._astar(startNode, endNode);
      if (!nodePath) return null;

      // Check if all nodes are at the same Y
      for (const node of nodePath) {
        if (Math.abs(node.y - surfaceY) > STEP_HEIGHT) return null; // Mixed heights
        nodeSet.add(node);
      }
    }

    // Add same-Y walk neighbors (one ring of padding for alternative routes)
    const toAdd = [];
    for (const node of nodeSet) {
      if (!node.neighbors) continue;
      for (const { node: neighbor } of node.neighbors) {
        if (Math.abs(neighbor.y - surfaceY) <= STEP_HEIGHT && !nodeSet.has(neighbor)) {
          toAdd.push(neighbor);
        }
      }
    }
    for (const n of toAdd) nodeSet.add(n);

    return [...nodeSet];
  }

  /**
   * Build a unified walkability grid spanning multiple same-Y surfaces.
   * Applies edge margins only at outer edges (not at shared edges between surfaces).
   * @private
   */
  _buildMergedPlaneGrid(nodes, surfaceY) {
    const CELL_SIZE = 0.5;
    const EDGE_MARGIN = 0.7;
    const HALF_PROBE = 0.9 / 2; // CHARACTER_WIDTH / 2
    const WALL_MARGIN = 0.15;
    const CHAR_HEIGHT = 2.0;
    const MAX_CELLS = 40000;

    // Compute bounding box
    let minX = Infinity, minZ = Infinity, maxX = -Infinity, maxZ = -Infinity;
    for (const node of nodes) {
      minX = Math.min(minX, node.minX);
      minZ = Math.min(minZ, node.minZ);
      maxX = Math.max(maxX, node.maxX);
      maxZ = Math.max(maxZ, node.maxZ);
    }

    const gw = Math.ceil((maxX - minX) / CELL_SIZE);
    const gh = Math.ceil((maxZ - minZ) / CELL_SIZE);

    if (gw * gh > MAX_CELLS || gw <= 0 || gh <= 0) return null;

    // Build grid
    const cells = [];
    for (let cx = 0; cx < gw; cx++) {
      cells[cx] = [];
      for (let cz = 0; cz < gh; cz++) {
        const worldX = minX + (cx + 0.5) * CELL_SIZE;
        const worldZ = minZ + (cz + 0.5) * CELL_SIZE;

        // Find host node (which surface this cell belongs to)
        let hostNode = null;
        for (const node of nodes) {
          if (worldX >= node.minX && worldX < node.maxX &&
              worldZ >= node.minZ && worldZ < node.maxZ) {
            hostNode = node;
            break;
          }
        }

        if (!hostNode) {
          cells[cx][cz] = false;
          continue;
        }

        // Check outer edge margins (only for edges not shared with another surface)
        let atOuterEdge = false;
        const dMinX = worldX - hostNode.minX;
        const dMaxX = hostNode.maxX - worldX;
        const dMinZ = worldZ - hostNode.minZ;
        const dMaxZ = hostNode.maxZ - worldZ;

        if (dMinX < EDGE_MARGIN &&
            !this._hasFloorBeyondEdge(nodes, hostNode.minX, worldZ, surfaceY, -1, 0)) {
          atOuterEdge = true;
        }
        if (!atOuterEdge && dMaxX < EDGE_MARGIN &&
            !this._hasFloorBeyondEdge(nodes, hostNode.maxX, worldZ, surfaceY, 1, 0)) {
          atOuterEdge = true;
        }
        if (!atOuterEdge && dMinZ < EDGE_MARGIN &&
            !this._hasFloorBeyondEdge(nodes, worldX, hostNode.minZ, surfaceY, 0, -1)) {
          atOuterEdge = true;
        }
        if (!atOuterEdge && dMaxZ < EDGE_MARGIN &&
            !this._hasFloorBeyondEdge(nodes, worldX, hostNode.maxZ, surfaceY, 0, 1)) {
          atOuterEdge = true;
        }

        if (atOuterEdge) {
          cells[cx][cz] = false;
          continue;
        }

        // Check for obstacles (walls, pillars) above the surface
        const queryBox = {
          minX: worldX - HALF_PROBE - WALL_MARGIN,
          maxX: worldX + HALF_PROBE + WALL_MARGIN,
          minY: surfaceY + 0.01,
          maxY: surfaceY + CHAR_HEIGHT,
          minZ: worldZ - HALF_PROBE - WALL_MARGIN,
          maxZ: worldZ + HALF_PROBE + WALL_MARGIN
        };

        const obstacles = this.collisionSystem.queryCuboids(queryBox);
        let isBlocked = false;
        for (const obs of obstacles) {
          if (obs.hasNoCollision && obs.hasNoCollision()) continue;
          const obsTop = obs.y + obs.height;
          const obsBottom = obs.y;
          if (Math.abs(obsTop - surfaceY) < 0.1) continue; // Floor itself
          if (obsTop <= surfaceY + 0.01) continue; // Below surface
          if (obsBottom < surfaceY + CHAR_HEIGHT) {
            if (queryBox.minX < obs.x + obs.width && queryBox.maxX > obs.x &&
                queryBox.minZ < obs.z + obs.depth && queryBox.maxZ > obs.z) {
              isBlocked = true;
              break;
            }
          }
        }

        cells[cx][cz] = !isBlocked;
      }
    }

    return new MergedPlaneGrid(cells, gw, gh, minX, minZ, CELL_SIZE, surfaceY);
  }

  /**
   * Check if any surface in the node set covers a point just beyond an edge.
   * Used to determine if an edge is "shared" (another surface continues) or
   * "outer" (ledge/void — needs edge margin).
   * @param {Array} nodes - All nodes in the merged set
   * @param {number} edgeX - X coordinate of the edge
   * @param {number} edgeZ - Z coordinate of the edge
   * @param {number} surfaceY - Surface Y level
   * @param {number} probeX - X probe direction (-1, 0, or 1)
   * @param {number} probeZ - Z probe direction (-1, 0, or 1)
   * @returns {boolean}
   * @private
   */
  _hasFloorBeyondEdge(nodes, edgeX, edgeZ, surfaceY, probeDirX, probeDirZ) {
    const PROBE_DIST = 0.3;
    const Y_TOL = 0.3;
    const px = edgeX + probeDirX * PROBE_DIST;
    const pz = edgeZ + probeDirZ * PROBE_DIST;

    for (const node of nodes) {
      if (Math.abs(node.y - surfaceY) > Y_TOL) continue;
      if (px >= node.minX && px < node.maxX &&
          pz >= node.minZ && pz < node.maxZ) {
        return true;
      }
    }
    return false;
  }

  /**
   * Find path across multiple surfaces using hierarchical A*
   * @private
   */
  _findPathAcrossSurfaces(startNode, endNode, start, end) {
    const blockedEdges = new Set();
    const MAX_RETRIES = 3;

    for (let retry = 0; retry <= MAX_RETRIES; retry++) {
      // Phase 1: High-level A* to find surface sequence
      const nodePath = this._astar(startNode, endNode, blockedEdges.size > 0 ? blockedEdges : null);

      if (!nodePath || nodePath.length === 0) {
        if (retry > 0) {
          Debug.warn('PathFinder', 'A* found no alternative path after blocking', blockedEdges.size, 'edges');
        } else {
          Debug.warn('PathFinder', 'A* found no path between surfaces');
        }
        return null;
      }

      // DEBUG: Log the A* path
      Debug.log('PathFinder', 'A* raw path has', nodePath.length, 'nodes (attempt', retry + 1, '):');
      for (let i = 0; i < nodePath.length; i++) {
        const n = nodePath[i];
        Debug.log('PathFinder', '  [' + i + '] ' + n.cuboidKey + ' Y=' + n.y.toFixed(1) +
                  ' center=(' + n.x.toFixed(1) + ',' + n.z.toFixed(1) + ')' +
                  ' bounds=(' + n.minX.toFixed(1) + '-' + n.maxX.toFixed(1) + ',' +
                  n.minZ.toFixed(1) + '-' + n.maxZ.toFixed(1) + ')');
      }

      // Analyze path for optimization opportunities
      const optimizedPath = this._optimizeNodePath(nodePath, start, end);

      // Phase 2: Identify staircase sequences (consecutive overlapping nodes)
      // and find common accessible directions for each sequence
      const staircaseInfo = this._analyzeStaircaseSequences(optimizedPath, start, end);

      // Phase 3: Compose detailed path through all surfaces
      const result = this._composeDetailedPath(optimizedPath, staircaseInfo, start, end);

      if (result.success) {
        return this._smoothPathWithHeightChanges(result.fullPath);
      }

      // Composition failed at a specific edge - block it and retry A*
      if (result.failedFromKey && result.failedToKey) {
        const edgeKey = result.failedFromKey + '->' + result.failedToKey;
        blockedEdges.add(edgeKey);
        Debug.log('PathFinder', 'Blocking edge', edgeKey, 'and retrying A* (attempt', retry + 2, ')');
      } else {
        // Composition failed but no specific edge to block - return partial path
        Debug.warn('PathFinder', 'Path composition failed with no specific edge to block');
        if (result.fullPath.length > 0) {
          return this._smoothPathWithHeightChanges(result.fullPath);
        }
        return null;
      }
    }

    Debug.warn('PathFinder', 'Exhausted all A* retries');
    return null;
  }

  /**
   * Compose a detailed waypoint path through a sequence of surfaces.
   * Returns {success, fullPath, failedFromKey, failedToKey} where success indicates
   * whether all transitions were composed without failure.
   * @private
   */
  _composeDetailedPath(optimizedPath, staircaseInfo, start, end) {
    const fullPath = [];
    let currentPos = { x: start.x, z: start.z };

    for (let i = 0; i < optimizedPath.length; i++) {
      const currentNode = optimizedPath[i];
      const isLastNode = i === optimizedPath.length - 1;

      // --- SAME-Y RUN DETECTION ---
      // When consecutive nodes share the same Y level, use merged-plane pathfinding
      // to produce a direct path across all of them. This avoids per-surface
      // edge transitions (which create zigzag paths through face centers) and
      // properly handles gaps between surfaces (blocks cells with no floor).
      if (!isLastNode && this.collisionSystem) {
        const nextNodeY = optimizedPath[i + 1].y;
        if (Math.abs(currentNode.y - nextNodeY) < 0.3) {
          // Find extent of same-Y run
          let runEnd = i + 1;
          while (runEnd + 1 < optimizedPath.length &&
                 Math.abs(optimizedPath[runEnd + 1].y - currentNode.y) < 0.3) {
            runEnd++;
          }

          const runResult = this._handleSameYRun(
            optimizedPath, i, runEnd, staircaseInfo, currentPos, start, end
          );
          if (runResult) {
            fullPath.push(...runResult.waypoints);
            currentPos = runResult.currentPos;
            i = runResult.nextIndex - 1; // -1 because loop will i++
            continue;
          }
          // Fall through to existing per-surface handling if merged fails
        }
      }

      if (isLastNode) {
        // Last surface: navigate to final destination
        const surfacePath = this._pathWithinSurface(
          currentNode,
          currentPos,
          { x: end.x, z: end.z }
        );
        fullPath.push(...surfacePath);
      } else {
        // Intermediate surface: navigate to transition point
        const nextNode = optimizedPath[i + 1];

        // Check if this is a purely vertical transition (stacked surfaces)
        const isVerticalStack = this._isVerticalTransition(currentNode, nextNode);

        // Get transition info - use staircase-aware version if applicable
        let transitionInfo;
        const staircaseDir = staircaseInfo.get(i);
        if (staircaseDir && isVerticalStack) {
          // Use the common accessible direction for this staircase
          transitionInfo = this._getStaircaseTransition(currentNode, nextNode, staircaseDir, currentPos);
        }

        // If staircase transition failed or wasn't applicable, try regular transition
        if (!transitionInfo) {
          transitionInfo = this._getTransitionInfo(currentNode, nextNode, currentPos, end);
        }

        if (!transitionInfo) {
          // Ultimate fallback: use edge-based points to avoid skipping nodes entirely
          Debug.warn('PathFinder', 'Could not find transition between nodes, using edge fallback');
          const heightDiff = Math.abs(nextNode.y - currentNode.y);
          const type = heightDiff > 1.0 ? (nextNode.y > currentNode.y ? 'jump' : 'fall') : 'walk';

          // For vertical transitions, find shared XZ region
          if (heightDiff > 1.0) {
            const overlapMinX = Math.max(currentNode.minX, nextNode.minX);
            const overlapMaxX = Math.min(currentNode.maxX, nextNode.maxX);
            const overlapMinZ = Math.max(currentNode.minZ, nextNode.minZ);
            const overlapMaxZ = Math.min(currentNode.maxZ, nextNode.maxZ);

            // Use center of overlap if it exists, otherwise use edge fallback
            if (overlapMinX < overlapMaxX && overlapMinZ < overlapMaxZ) {
              const transitionX = (overlapMinX + overlapMaxX) / 2;
              const transitionZ = (overlapMinZ + overlapMaxZ) / 2;
              transitionInfo = {
                type,
                exitPoint: { x: transitionX, z: transitionZ },
                landingPoint: { x: transitionX, z: transitionZ }
              };
            } else {
              // Edge-adjacent vertical - use shared transition point
              const sharedPoint = this._findVerticalEdgeTransitionPoint(currentNode, nextNode);
              transitionInfo = {
                type,
                exitPoint: sharedPoint,
                landingPoint: { x: sharedPoint.x, z: sharedPoint.z }
              };
            }
          } else {
            // Walk transition - edge points can differ
            transitionInfo = {
              type,
              exitPoint: this._findEdgePointFallback(currentNode, nextNode),
              landingPoint: this._findEdgePointFallback(nextNode, currentNode)
            };
          }
        }

        // For vertical stacks, check if we can skip the intermediate surface path
        // if current position is already near the transition point
        const distToExit = Math.sqrt(
          Math.pow(transitionInfo.exitPoint.x - currentPos.x, 2) +
          Math.pow(transitionInfo.exitPoint.z - currentPos.z, 2)
        );

        if (isVerticalStack && distToExit < 0.5) {
          // Already at transition point, just add the landing
          fullPath.push({
            x: transitionInfo.landingPoint.x,
            y: nextNode.y,
            z: transitionInfo.landingPoint.z,
            type: transitionInfo.type
          });
        } else {
          // Path to transition point on current surface
          Debug.log('PathFinder', 'Surface path needed from',
                    currentPos.x.toFixed(1), currentPos.z.toFixed(1),
                    'to exit', transitionInfo.exitPoint.x.toFixed(1), transitionInfo.exitPoint.z.toFixed(1),
                    'on node Y=', currentNode.y);

          let surfacePath = this._pathWithinSurface(
            currentNode,
            currentPos,
            transitionInfo.exitPoint
          );

          Debug.log('PathFinder', 'Surface path result:', surfacePath ? surfacePath.length + ' waypoints' : 'EMPTY',
                    surfacePath && surfacePath.length > 0 ?
                      'first=' + surfacePath[0].x.toFixed(1) + ',' + surfacePath[0].z.toFixed(1) +
                      ' last=' + surfacePath[surfacePath.length-1].x.toFixed(1) + ',' + surfacePath[surfacePath.length-1].z.toFixed(1)
                      : '');

          // If surface path is empty, the transition point may be unreachable from current position
          if (!surfacePath || surfacePath.length === 0) {
            Debug.log('PathFinder', 'Surface path to transition point failed, trying alternative staircase directions');

            // For vertical stacks, try alternative edge directions before other fallbacks
            if (isVerticalStack) {
              const altEdgeNames = ['west', 'east', 'south', 'north'];
              for (const edgeName of altEdgeNames) {
                // Skip the already-tried direction
                if (staircaseDir && edgeName === staircaseDir.name) continue;

                const altDir = {
                  name: edgeName,
                  axis: (edgeName === 'west' || edgeName === 'east') ? 'x' : 'z',
                  dir: (edgeName === 'west' || edgeName === 'south') ? -1 : 1
                };

                const altTransition = this._getStaircaseTransition(
                  currentNode, nextNode, altDir, currentPos
                );

                if (altTransition) {
                  const altPath = this._pathWithinSurface(
                    currentNode, currentPos, altTransition.exitPoint
                  );
                  if (altPath && altPath.length > 0) {
                    transitionInfo = altTransition;
                    surfacePath = altPath;
                    Debug.log('PathFinder', 'Found working alternative staircase direction:', edgeName);
                    break;
                  }
                }
              }
            }

            // If still no path, try existing fallback mechanisms
            if (!surfacePath || surfacePath.length === 0) {
              Debug.log('PathFinder', 'Alternative directions failed, trying reachable transition search');

              // Try to find a reachable transition point by testing multiple candidates
              const alternativeTransition = this._findReachableTransition(
                currentNode, nextNode, currentPos, end
              );

              if (alternativeTransition) {
                transitionInfo = alternativeTransition.transitionInfo;
                surfacePath = alternativeTransition.surfacePath;
                Debug.log('PathFinder', 'Found alternative reachable transition');
              } else {
                // No reachable transition - this path segment is blocked
                // Try to get as close as possible to any transition point
                Debug.warn('PathFinder', 'No reachable transition found, attempting closest approach');
                const closestApproach = this._findClosestApproachToTransition(
                  currentNode, nextNode, currentPos, end
                );
                if (closestApproach && closestApproach.surfacePath.length > 0) {
                  surfacePath = closestApproach.surfacePath;
                  transitionInfo = closestApproach.transitionInfo;
                }
              }
            }
          }

          if (surfacePath && surfacePath.length > 0) {
            fullPath.push(...surfacePath);
          } else if (isVerticalStack) {
            // Staircase step completely failed - don't add raw waypoints through walls.
            // Signal failure so A* can retry with this edge blocked.
            Debug.warn('PathFinder', 'Staircase transition completely failed at',
                        currentNode.cuboidKey, '->', nextNode.cuboidKey, ', requesting A* retry');
            return {
              success: false,
              fullPath,
              failedFromKey: currentNode.cuboidKey,
              failedToKey: nextNode.cuboidKey
            };
          }

          // Add transition waypoint
          fullPath.push({
            x: transitionInfo.landingPoint.x,
            y: nextNode.y,
            z: transitionInfo.landingPoint.z,
            type: transitionInfo.type
          });
        }

        currentPos = transitionInfo.landingPoint;
      }
    }

    return { success: true, fullPath };
  }

  /**
   * Handle a run of consecutive same-Y surfaces using merged-plane pathfinding.
   * Produces a direct path across all surfaces in the run, avoiding per-surface
   * edge transitions that create zigzag paths through face centers.
   * @param {Array} optimizedPath - Full node path
   * @param {number} runStart - Index of first node in the run
   * @param {number} runEnd - Index of last node in the run
   * @param {Map} staircaseInfo - Staircase direction info from analysis
   * @param {Object} currentPos - Current position {x, z}
   * @param {Object} start - Original start position
   * @param {Object} end - Original end position (destination)
   * @returns {Object|null} {waypoints, currentPos, nextIndex} or null if failed
   * @private
   */
  _handleSameYRun(optimizedPath, runStart, runEnd, staircaseInfo, currentPos, start, end) {
    const surfaceY = optimizedPath[runStart].y;
    const isRunAtEnd = runEnd === optimizedPath.length - 1;

    // Collect run nodes + one ring of same-Y walk neighbors for the merged grid
    const nodeSet = new Set();
    for (let j = runStart; j <= runEnd; j++) {
      nodeSet.add(optimizedPath[j]);
    }

    // Add same-Y walk neighbors (one ring for alternative routes)
    const toAdd = [];
    for (const node of nodeSet) {
      if (!node.neighbors) continue;
      for (const { node: neighbor } of node.neighbors) {
        if (Math.abs(neighbor.y - surfaceY) <= 0.3 && !nodeSet.has(neighbor)) {
          toAdd.push(neighbor);
        }
      }
    }
    for (const n of toAdd) nodeSet.add(n);
    const allNodes = [...nodeSet];

    // Determine exit target
    let exitTarget;
    let afterRunTransition = null;
    let afterRunNode = null;

    if (isRunAtEnd) {
      // Run extends to the end of the path — destination is the exit target
      exitTarget = { x: end.x, z: end.z };
    } else {
      // Compute transition from last run node to the next node (different Y)
      afterRunNode = optimizedPath[runEnd + 1];
      const lastRunNode = optimizedPath[runEnd];

      // Try staircase transition first if applicable
      const staircaseDir = staircaseInfo.get(runEnd);
      const isVertical = this._isVerticalTransition(lastRunNode, afterRunNode);

      if (staircaseDir && isVertical) {
        afterRunTransition = this._getStaircaseTransition(
          lastRunNode, afterRunNode, staircaseDir, currentPos
        );
      }
      if (!afterRunTransition) {
        afterRunTransition = this._getTransitionInfo(
          lastRunNode, afterRunNode, currentPos, end
        );
      }

      if (!afterRunTransition) return null; // Can't determine exit, fall back
      exitTarget = afterRunTransition.exitPoint;
    }

    // Build merged grid
    const mergedGrid = this._buildMergedPlaneGrid(allNodes, surfaceY);
    if (!mergedGrid) return null;

    // Run A* on merged grid
    const pathfinder = new GridPathfinder(mergedGrid);
    const gridPath = pathfinder.findPathWorld(
      { x: currentPos.x, z: currentPos.z },
      { x: exitTarget.x, z: exitTarget.z }
    );

    if (!gridPath || gridPath.length === 0) return null;

    // Verify the path reaches near the exit target
    const lastPoint = gridPath[gridPath.length - 1];
    const distToExit = Math.sqrt(
      (lastPoint.x - exitTarget.x) ** 2 + (lastPoint.z - exitTarget.z) ** 2
    );
    if (distToExit > 3.0) return null; // Too far from target — fall back

    // Convert to waypoints
    let waypoints = gridPath.map(p => ({
      x: p.x, y: surfaceY, z: p.z, type: 'walk'
    }));

    // Smooth the path using line-of-sight checks
    waypoints = this._smoothPath(waypoints, surfaceY);

    // Determine what comes after the run
    let newCurrentPos;
    let nextIndex;

    if (isRunAtEnd) {
      // The merged path goes to the destination — we're done
      newCurrentPos = { x: end.x, z: end.z };
      nextIndex = optimizedPath.length; // Exit the loop
    } else {
      // Bridge the gap between the merged path's end and the transition exit point.
      // The merged A* may end slightly short of the exit (snapped to nearest walkable cell),
      // so add an explicit exit waypoint to ensure proper approach to the height transition.
      const lastWp = waypoints[waypoints.length - 1];
      const gapToExit = Math.sqrt(
        (lastWp.x - exitTarget.x) ** 2 + (lastWp.z - exitTarget.z) ** 2
      );
      if (gapToExit > 0.3) {
        waypoints.push({
          x: exitTarget.x, y: surfaceY, z: exitTarget.z, type: 'walk'
        });
      }

      // Add transition waypoint to the next level
      waypoints.push({
        x: afterRunTransition.landingPoint.x,
        y: afterRunNode.y,
        z: afterRunTransition.landingPoint.z,
        type: afterRunTransition.type
      });
      newCurrentPos = afterRunTransition.landingPoint;
      nextIndex = runEnd + 1; // Continue from the node after the run
    }

    Debug.log('PathFinder', 'Same-Y run handled via merged-plane:',
              'nodes', runStart, '-', runEnd,
              'waypoints:', waypoints.length,
              'exitTarget:', exitTarget.x.toFixed(1), exitTarget.z.toFixed(1));

    return { waypoints, currentPos: newCurrentPos, nextIndex };
  }

  /**
   * Analyze path for staircase sequences (consecutive overlapping surfaces)
   * and find common accessible directions for climbing/descending
   * @param {Array} nodePath - Sequence of nodes to traverse
   * @param {Object} start - Starting position
   * @param {Object} end - Target destination
   * @returns {Map<number, Object>} Map of node index -> staircase direction info
   * @private
   */
  _analyzeStaircaseSequences(nodePath, start, end) {
    const staircaseDirections = new Map();

    // Find sequences of overlapping nodes
    let sequenceStart = -1;
    let sequenceNodes = [];

    for (let i = 0; i < nodePath.length - 1; i++) {
      const current = nodePath[i];
      const next = nodePath[i + 1];
      const overlaps = current.overlapsXZ(next);

      if (overlaps) {
        if (sequenceStart === -1) {
          sequenceStart = i;
          sequenceNodes = [current];
        }
        sequenceNodes.push(next);
      } else if (sequenceStart !== -1) {
        // End of sequence - find common accessible direction
        if (sequenceNodes.length >= 2) {
          const direction = this._findCommonAccessibleDirection(sequenceNodes, start, end);
          if (direction) {
            for (let j = sequenceStart; j < i; j++) {
              staircaseDirections.set(j, direction);
            }
          }
        }
        sequenceStart = -1;
        sequenceNodes = [];
      }
    }

    // Handle sequence that extends to end of path
    if (sequenceStart !== -1 && sequenceNodes.length >= 2) {
      const direction = this._findCommonAccessibleDirection(sequenceNodes, start, end);
      if (direction) {
        for (let j = sequenceStart; j < nodePath.length - 1; j++) {
          staircaseDirections.set(j, direction);
        }
      }
    }

    return staircaseDirections;
  }

  /**
   * Find a common edge direction that's accessible for all nodes in a staircase
   * @param {Array} nodes - Sequence of overlapping nodes
   * @param {Object} start - Starting position
   * @param {Object} end - Target destination
   * @returns {Object|null} Direction info {edge: 'west'|'east'|'south'|'north', x/z: coordinate}
   * @private
   */
  _findCommonAccessibleDirection(nodes, start, end) {
    if (nodes.length < 2) return null;

    // Find the common overlap region for all nodes
    let overlapMinX = -Infinity, overlapMaxX = Infinity;
    let overlapMinZ = -Infinity, overlapMaxZ = Infinity;

    for (const node of nodes) {
      overlapMinX = Math.max(overlapMinX, node.minX);
      overlapMaxX = Math.min(overlapMaxX, node.maxX);
      overlapMinZ = Math.max(overlapMinZ, node.minZ);
      overlapMaxZ = Math.min(overlapMaxZ, node.maxZ);
    }

    // If no common overlap, can't use a common direction
    if (overlapMinX >= overlapMaxX || overlapMinZ >= overlapMaxZ) {
      return null;
    }

    // Define edges to try
    const edges = [
      { name: 'west', axis: 'x', coord: overlapMinX, dir: -1 },
      { name: 'east', axis: 'x', coord: overlapMaxX, dir: 1 },
      { name: 'south', axis: 'z', coord: overlapMinZ, dir: -1 },
      { name: 'north', axis: 'z', coord: overlapMaxZ, dir: 1 }
    ];

    // Score edges by accessibility and distance from START (prefer closest to player)
    const scoredEdges = [];
    const startX = start ? start.x : nodes[0].x;
    const startZ = start ? start.z : nodes[0].z;
    const centerX = (overlapMinX + overlapMaxX) / 2;
    const centerZ = (overlapMinZ + overlapMaxZ) / 2;

    for (const edge of edges) {
      // Check if ALL transitions in the staircase can use this edge
      let allAccessible = true;

      for (let i = 0; i < nodes.length - 1; i++) {
        const lowerNode = nodes[i].y < nodes[i + 1].y ? nodes[i] : nodes[i + 1];
        const upperNode = nodes[i].y < nodes[i + 1].y ? nodes[i + 1] : nodes[i];

        // Use PAIRWISE overlap for this specific transition, not the total overlap
        // This is critical for multi-level staircases where intermediate surfaces extend further
        const pairOverlapMinX = Math.max(lowerNode.minX, upperNode.minX);
        const pairOverlapMaxX = Math.min(lowerNode.maxX, upperNode.maxX);
        const pairOverlapMinZ = Math.max(lowerNode.minZ, upperNode.minZ);
        const pairOverlapMaxZ = Math.min(lowerNode.maxZ, upperNode.maxZ);

        // Check at the center of THIS pair's overlap
        const checkX = (pairOverlapMinX + pairOverlapMaxX) / 2;
        const checkZ = (pairOverlapMinZ + pairOverlapMaxZ) / 2;

        // Check if there's a wall blocking this edge
        // IMPORTANT: Use the PAIRWISE overlap edge for wall detection, not the global overlap edge!
        // For floor→red transition, we want to check red's edge (X=-16), not blue's edge (X=-8)
        let wallCheckX, wallCheckZ;
        if (edge.axis === 'x') {
          // Use the pairwise overlap edge coordinate in this direction
          wallCheckX = edge.dir < 0 ? pairOverlapMinX : pairOverlapMaxX;
          wallCheckZ = checkZ;
        } else {
          wallCheckX = checkX;
          // Use the pairwise overlap edge coordinate in this direction
          wallCheckZ = edge.dir < 0 ? pairOverlapMinZ : pairOverlapMaxZ;
        }

        const hasWall = this._hasObstacleBetweenLevels(wallCheckX, wallCheckZ, lowerNode.y, upperNode.y);
        if (hasWall) {
          Debug.log('PathFinder', 'Edge', edge.name, 'blocked by wall at', wallCheckX.toFixed(1), wallCheckZ.toFixed(1));
          allAccessible = false;
          break;
        }

        // Check step-up headroom: when stepping from lower to upper, we need headroom
        // from lowerY+HEIGHT to upperY+HEIGHT at the exit point (on lower surface, outside overlap)
        // The exit point is outside the overlap in the approach direction
        let exitCheckX, exitCheckZ;
        if (edge.axis === 'x') {
          exitCheckX = edge.dir < 0 ? pairOverlapMinX - 0.6 : pairOverlapMaxX + 0.6;
          exitCheckZ = checkZ;
        } else {
          exitCheckX = checkX;
          exitCheckZ = edge.dir < 0 ? pairOverlapMinZ - 0.6 : pairOverlapMaxZ + 0.6;
        }
        // Clamp to lower surface bounds
        exitCheckX = Math.max(lowerNode.minX + 0.3, Math.min(lowerNode.maxX - 0.3, exitCheckX));
        exitCheckZ = Math.max(lowerNode.minZ + 0.3, Math.min(lowerNode.maxZ - 0.3, exitCheckZ));

        const stepUpBlocked = this._hasStepUpHeadroomBlock(exitCheckX, exitCheckZ, lowerNode.y, upperNode.y);
        if (stepUpBlocked) {
          Debug.log('PathFinder', 'Edge', edge.name, 'blocked by step-up headroom at', exitCheckX.toFixed(1), exitCheckZ.toFixed(1));
          allAccessible = false;
          break;
        }

        // Check if the TRANSITION AREA is physically walkable on both surfaces.
        // Use points near the edge being tested, NOT the overlap center.
        // For narrow surfaces (width=1), the overlap center is deep inside staircase
        // obstacles from upper levels, falsely blocking the walkability check.
        // The exit point (outside overlap) is the right check for the lower surface,
        // and a point just inside the overlap near the edge is right for the upper surface.
        let walkCheckLowerX, walkCheckLowerZ, walkCheckUpperX, walkCheckUpperZ;
        if (edge.axis === 'x') {
          // X-axis edge: approach from X direction
          walkCheckLowerX = exitCheckX; // Already outside overlap, clamped to lower surface
          walkCheckLowerZ = exitCheckZ;
          walkCheckUpperX = edge.dir < 0 ? pairOverlapMinX + 0.3 : pairOverlapMaxX - 0.3;
          walkCheckUpperZ = exitCheckZ;
        } else {
          // Z-axis edge: approach from Z direction
          walkCheckLowerX = exitCheckX;
          walkCheckLowerZ = exitCheckZ;
          walkCheckUpperX = exitCheckX;
          walkCheckUpperZ = edge.dir < 0 ? pairOverlapMinZ + 0.3 : pairOverlapMaxZ - 0.3;
        }
        // Clamp upper check to upper surface bounds
        walkCheckUpperX = Math.max(upperNode.minX + 0.3, Math.min(upperNode.maxX - 0.3, walkCheckUpperX));
        walkCheckUpperZ = Math.max(upperNode.minZ + 0.3, Math.min(upperNode.maxZ - 0.3, walkCheckUpperZ));

        const lowerWalkable = this._isPointPhysicallyWalkableOnNode(lowerNode, walkCheckLowerX, walkCheckLowerZ);
        const upperWalkable = this._isPointPhysicallyWalkableOnNode(upperNode, walkCheckUpperX, walkCheckUpperZ);

        if (!lowerWalkable || !upperWalkable) {
          Debug.log('PathFinder', 'Edge', edge.name, 'not physically walkable: lower=', lowerWalkable,
                      'at', walkCheckLowerX.toFixed(1), walkCheckLowerZ.toFixed(1),
                      'upper=', upperWalkable,
                      'at', walkCheckUpperX.toFixed(1), walkCheckUpperZ.toFixed(1),
                      'lowerY=', lowerNode.y, 'upperY=', upperNode.y);
          allAccessible = false;
          break;
        }
      }

      if (allAccessible) {
        // Check if ALL intermediate surfaces extend beyond the overlap in this direction
        // This determines if we can WALK along each surface to reach the transition edge
        // We need approach room on every node EXCEPT the topmost (destination)
        const sortedByY = [...nodes].sort((a, b) => a.y - b.y);
        const nodesToCheck = sortedByY.slice(0, -1); // All except highest (destination)

        let hasApproachRoom = true;
        for (const node of nodesToCheck) {
          let nodeHasRoom = false;
          if (edge.axis === 'x') {
            if (edge.dir < 0) {
              // West edge: surface should extend west of overlap
              nodeHasRoom = node.minX < overlapMinX - 0.5;
            } else {
              // East edge: surface should extend east of overlap
              nodeHasRoom = node.maxX > overlapMaxX + 0.5;
            }
          } else {
            if (edge.dir < 0) {
              // South edge: surface should extend south of overlap
              nodeHasRoom = node.minZ < overlapMinZ - 0.5;
            } else {
              // North edge: surface should extend north of overlap
              nodeHasRoom = node.maxZ > overlapMaxZ + 0.5;
            }
          }
          if (!nodeHasRoom) {
            Debug.log('PathFinder', 'Edge', edge.name, 'no approach room on node Y=', node.y,
                        'bounds:', node.minX.toFixed(1), '-', node.maxX.toFixed(1),
                        ',', node.minZ.toFixed(1), '-', node.maxZ.toFixed(1));
            hasApproachRoom = false;
            break;
          }
        }

        // Calculate score: PREFER EDGES CLOSEST TO START (where player is coming from)
        let edgeCenterX, edgeCenterZ;
        if (edge.axis === 'x') {
          edgeCenterX = edge.coord;
          edgeCenterZ = centerZ;
        } else {
          edgeCenterX = centerX;
          edgeCenterZ = edge.coord;
        }
        // Distance from START - lower is better (player is closer to this edge)
        const distFromStart = Math.sqrt(
          Math.pow(edgeCenterX - startX, 2) + Math.pow(edgeCenterZ - startZ, 2)
        );

        // Penalize edges without approach room - add large penalty to score
        const approachPenalty = hasApproachRoom ? 0 : 1000;
        const finalScore = distFromStart + approachPenalty;

        Debug.log('PathFinder', 'Edge', edge.name, 'accessible, distFromStart:', distFromStart.toFixed(1),
                    'hasApproachRoom:', hasApproachRoom, 'finalScore:', finalScore.toFixed(1));
        scoredEdges.push({ edge, score: finalScore });
      }
    }

    if (scoredEdges.length === 0) {
      Debug.warn('PathFinder', 'No common accessible edge found for staircase');
      return null;
    }

    // Pick the edge closest to destination
    scoredEdges.sort((a, b) => a.score - b.score);
    return scoredEdges[0].edge;
  }

  /**
   * Get transition points using a pre-determined staircase direction
   * @param {NavNode} fromNode - Source node
   * @param {NavNode} toNode - Destination node
   * @param {Object} direction - The common edge direction
   * @param {Object} currentPos - Current position
   * @returns {Object} Transition info {type, exitPoint, landingPoint}
   * @private
   */
  _getStaircaseTransition(fromNode, toNode, direction, currentPos) {
    const heightDiff = toNode.y - fromNode.y;
    const goingUp = heightDiff > 0;
    const type = Math.abs(heightDiff) > 1.0 ? (goingUp ? 'jump' : 'fall') : 'walk';

    const EDGE_MARGIN = 0.5;
    const APPROACH_OFFSET = 0.6; // How far outside overlap to place exit point
    const STEP = 0.5; // Step size for searching valid positions

    // Find the overlap region (where upper surface sits on lower surface)
    const overlapMinX = Math.max(fromNode.minX, toNode.minX);
    const overlapMaxX = Math.min(fromNode.maxX, toNode.maxX);
    const overlapMinZ = Math.max(fromNode.minZ, toNode.minZ);
    const overlapMaxZ = Math.min(fromNode.maxZ, toNode.maxZ);

    const lowerNode = goingUp ? fromNode : toNode;
    const upperNode = goingUp ? toNode : fromNode;

    // Helper to validate a transition point
    const isValidTransition = (exitX, exitZ, landingX, landingZ) => {
      const exitWalkable = this._isPointPhysicallyWalkableOnNode(fromNode, exitX, exitZ);
      const landingWalkable = this._isPointPhysicallyWalkableOnNode(toNode, landingX, landingZ);
      const transitionClear = !this._hasObstacleBetweenLevels(
        (exitX + landingX) / 2, (exitZ + landingZ) / 2,
        lowerNode.y, upperNode.y
      );

      // When going UP, check that the exit point has headroom for the full step-up.
      // The character's head must rise from lowerY+HEIGHT to upperY+HEIGHT during the step.
      // A ceiling at lowerY+HEIGHT would block the step-up even if standing is allowed.
      let stepUpHeadroomClear = true;
      if (goingUp) {
        stepUpHeadroomClear = !this._hasStepUpHeadroomBlock(exitX, exitZ, lowerNode.y, upperNode.y);
      }

      return exitWalkable && landingWalkable && transitionClear && stepUpHeadroomClear;
    };

    // Exit/landing positions depend on direction (up vs down):
    // Going UP: exit is OUTSIDE overlap (lower surface), landing is INSIDE overlap (upper surface)
    // Going DOWN: exit is INSIDE overlap (upper surface), landing is OUTSIDE overlap (lower surface)
    // The "outside" position is on the edge away from the overlap, "inside" is within the overlap

    const validTransitions = [];

    if (direction.axis === 'x') {
      // Approaching from X direction (west or east)
      // "Outside" position is away from overlap, "inside" is within overlap
      const outsideX = direction.dir < 0 ? overlapMinX - APPROACH_OFFSET : overlapMaxX + APPROACH_OFFSET;
      const insideX = direction.dir < 0 ? overlapMinX + EDGE_MARGIN : overlapMaxX - EDGE_MARGIN;

      // For going UP: exit=outside (lower surface), landing=inside (upper surface)
      // For going DOWN: exit=inside (upper surface), landing=outside (lower surface)
      const exitBaseX = goingUp ? outsideX : insideX;
      const landingBaseX = goingUp ? insideX : outsideX;

      // Clamp to respective surface bounds
      const exitX = Math.max(fromNode.minX + 0.3, Math.min(fromNode.maxX - 0.3, exitBaseX));
      const landingX = Math.max(toNode.minX + 0.3, Math.min(toNode.maxX - 0.3, landingBaseX));

      // Search along Z for a valid position
      const preferredZ = currentPos ?
        Math.max(overlapMinZ + 0.3, Math.min(overlapMaxZ - 0.3, currentPos.z)) :
        (overlapMinZ + overlapMaxZ) / 2;

      // Try the preferred position first
      if (isValidTransition(exitX, preferredZ, landingX, preferredZ)) {
        validTransitions.push({ exitX, exitZ: preferredZ, landingX, landingZ: preferredZ, dist: 0 });
      }

      // Search outward from preferred Z
      for (let offset = STEP; offset <= (overlapMaxZ - overlapMinZ) / 2 + STEP; offset += STEP) {
        for (const sign of [-1, 1]) {
          const z = preferredZ + sign * offset;
          if (z < overlapMinZ + 0.3 || z > overlapMaxZ - 0.3) continue;
          if (isValidTransition(exitX, z, landingX, z)) {
            validTransitions.push({ exitX, exitZ: z, landingX, landingZ: z, dist: offset });
          }
        }
      }
    } else {
      // Approaching from Z direction (south or north)
      const outsideZ = direction.dir < 0 ? overlapMinZ - APPROACH_OFFSET : overlapMaxZ + APPROACH_OFFSET;
      const insideZ = direction.dir < 0 ? overlapMinZ + EDGE_MARGIN : overlapMaxZ - EDGE_MARGIN;

      // For going UP: exit=outside (lower surface), landing=inside (upper surface)
      // For going DOWN: exit=inside (upper surface), landing=outside (lower surface)
      const exitBaseZ = goingUp ? outsideZ : insideZ;
      const landingBaseZ = goingUp ? insideZ : outsideZ;

      // Clamp to respective surface bounds
      const exitZ = Math.max(fromNode.minZ + 0.3, Math.min(fromNode.maxZ - 0.3, exitBaseZ));
      const landingZ = Math.max(toNode.minZ + 0.3, Math.min(toNode.maxZ - 0.3, landingBaseZ));

      // Search along X for a valid position
      const preferredX = currentPos ?
        Math.max(overlapMinX + 0.3, Math.min(overlapMaxX - 0.3, currentPos.x)) :
        (overlapMinX + overlapMaxX) / 2;

      // Try the preferred position first
      if (isValidTransition(preferredX, exitZ, preferredX, landingZ)) {
        validTransitions.push({ exitX: preferredX, exitZ, landingX: preferredX, landingZ, dist: 0 });
      }

      // Search outward from preferred X
      for (let offset = STEP; offset <= (overlapMaxX - overlapMinX) / 2 + STEP; offset += STEP) {
        for (const sign of [-1, 1]) {
          const x = preferredX + sign * offset;
          if (x < overlapMinX + 0.3 || x > overlapMaxX - 0.3) continue;
          if (isValidTransition(x, exitZ, x, landingZ)) {
            validTransitions.push({ exitX: x, exitZ, landingX: x, landingZ, dist: offset });
          }
        }
      }
    }

    // If no valid transitions found, return null
    if (validTransitions.length === 0) {
      Debug.log('PathFinder', '_getStaircaseTransition: No valid transition found for dir=', direction.name,
                'overlap=', overlapMinX.toFixed(1), '-', overlapMaxX.toFixed(1));
      return null;
    }

    // Pick the closest valid transition to the preferred position
    validTransitions.sort((a, b) => a.dist - b.dist);
    const best = validTransitions[0];

    const exitPoint = { x: best.exitX, z: best.exitZ };
    const landingPoint = { x: best.landingX, z: best.landingZ };

    Debug.log('PathFinder', '_getStaircaseTransition:', goingUp ? 'UP' : 'DOWN',
              'dir=', direction.name,
              'exit=', best.exitX.toFixed(1), best.exitZ.toFixed(1),
              'land=', best.landingX.toFixed(1), best.landingZ.toFixed(1),
              'overlap=', overlapMinX.toFixed(1), '-', overlapMaxX.toFixed(1));

    return { type, exitPoint, landingPoint };
  }

  /**
   * Check if transition between two nodes is purely vertical (stacked surfaces)
   * @private
   */
  _isVerticalTransition(fromNode, toNode) {
    // Check if one surface is directly above/below the other
    return fromNode.overlapsXZ(toNode) && Math.abs(fromNode.y - toNode.y) > 0.1;
  }

  /**
   * Optimize node path by identifying straight-line opportunities
   * For stacked staircases, this allows diagonal movement through multiple levels
   * @private
   */
  _optimizeNodePath(nodePath, start, end) {
    if (nodePath.length <= 2) return nodePath;

    // Check if path is a vertical stack (staircase scenario)
    // If all nodes overlap on XZ, they form a vertical column
    let allOverlap = true;
    for (let i = 1; i < nodePath.length; i++) {
      if (!nodePath[0].overlapsXZ(nodePath[i])) {
        allOverlap = false;
        break;
      }
    }

    // If all nodes overlap (vertical stack), check if we can use the straight-line path
    // This prevents zigzagging on staircases
    if (allOverlap) {
      // Find the common overlap region
      let overlapMinX = -Infinity, overlapMaxX = Infinity;
      let overlapMinZ = -Infinity, overlapMaxZ = Infinity;

      for (const node of nodePath) {
        overlapMinX = Math.max(overlapMinX, node.minX);
        overlapMaxX = Math.min(overlapMaxX, node.maxX);
        overlapMinZ = Math.max(overlapMinZ, node.minZ);
        overlapMaxZ = Math.min(overlapMaxZ, node.maxZ);
      }

      // Check if start->end line passes through the common overlap
      const pathThroughOverlap =
        this._lineIntersectsBox(start.x, start.z, end.x, end.z,
          overlapMinX, overlapMaxX, overlapMinZ, overlapMaxZ);

      if (pathThroughOverlap) {
        // Mark this as a straight-line staircase path
        // The transition logic will use the direct path
        return nodePath;
      }
    }

    return nodePath;
  }

  /**
   * Check if a line segment intersects a box in 2D (XZ plane)
   * @private
   */
  _lineIntersectsBox(x1, z1, x2, z2, minX, maxX, minZ, maxZ) {
    // Simple AABB line intersection test
    // Check if either endpoint is in the box
    if (x1 >= minX && x1 <= maxX && z1 >= minZ && z1 <= maxZ) return true;
    if (x2 >= minX && x2 <= maxX && z2 >= minZ && z2 <= maxZ) return true;

    // Check line-box intersection using parametric clipping
    const dx = x2 - x1;
    const dz = z2 - z1;

    let tMin = 0, tMax = 1;

    if (Math.abs(dx) > 0.0001) {
      const tx1 = (minX - x1) / dx;
      const tx2 = (maxX - x1) / dx;
      tMin = Math.max(tMin, Math.min(tx1, tx2));
      tMax = Math.min(tMax, Math.max(tx1, tx2));
    } else if (x1 < minX || x1 > maxX) {
      return false;
    }

    if (Math.abs(dz) > 0.0001) {
      const tz1 = (minZ - z1) / dz;
      const tz2 = (maxZ - z1) / dz;
      tMin = Math.max(tMin, Math.min(tz1, tz2));
      tMax = Math.min(tMax, Math.max(tz1, tz2));
    } else if (z1 < minZ || z1 > maxZ) {
      return false;
    }

    return tMin <= tMax;
  }

  /**
   * Get path within a surface from currentPos to targetPos
   * @private
   */
  _pathWithinSurface(node, currentPos, targetPos) {
    if (!node.hasGrid()) {
      // No grid - check line of sight before using direct path
      if (this._hasLineOfSight(currentPos, targetPos, node.y)) {
        return [{
          x: targetPos.x,
          y: node.y,
          z: targetPos.z,
          type: 'walk'
        }];
      }
      // No line of sight and no grid - can't pathfind properly
      // Return direct path as last resort (shouldn't happen with proper grid setup)
      return [{
        x: targetPos.x,
        y: node.y,
        z: targetPos.z,
        type: 'walk'
      }];
    }

    const gridPath = node.gridPathfinder.findPathWorld(currentPos, targetPos);

    Debug.log('PathFinder', '_pathWithinSurface gridPath:',
              gridPath ? gridPath.length + ' cells' : 'NULL',
              'from', currentPos.x.toFixed(1), currentPos.z.toFixed(1),
              'to', targetPos.x.toFixed(1), targetPos.z.toFixed(1));

    if (!gridPath || gridPath.length === 0) {
      Debug.log('PathFinder', '_pathWithinSurface: Grid path failed, trying alternatives');
      // GridPathfinder failed - try to find nearest reachable point to target
      const nearestWalkable = this._findWalkableNearPoint(node, targetPos.x, targetPos.z, 5);
      if (nearestWalkable) {
        const altPath = node.gridPathfinder.findPathWorld(currentPos, nearestWalkable);
        if (altPath && altPath.length > 0) {
          const waypoints = altPath.map(pos => ({
            x: pos.x,
            y: node.y,
            z: pos.z,
            type: 'walk'
          }));
          return this._smoothPath(waypoints, node.y);
        }
      }
      // Still can't find path - check if we have line of sight as last resort
      if (this._hasLineOfSight(currentPos, targetPos, node.y)) {
        Debug.warn('PathFinder', '_pathWithinSurface: Using line-of-sight fallback!');
        return [{
          x: targetPos.x,
          y: node.y,
          z: targetPos.z,
          type: 'walk'
        }];
      }
      // No valid path - return empty (will be handled by caller)
      Debug.warn('PathFinder', '_pathWithinSurface: No path found, returning empty');
      return [];
    }

    // Convert to waypoints
    const waypoints = gridPath.map(pos => ({
      x: pos.x,
      y: node.y,
      z: pos.z,
      type: 'walk'
    }));

    // Smooth within this surface
    return this._smoothPath(waypoints, node.y);
  }

  /**
   * Get transition information between two adjacent nodes
   * @param {NavNode} fromNode - Source node
   * @param {NavNode} toNode - Destination node
   * @param {Object} currentPos - Current position {x, z} (optional, for direction preference)
   * @param {Object} finalDest - Final destination {x, z} (optional, for direction preference)
   * @private
   */
  _getTransitionInfo(fromNode, toNode, currentPos = null, finalDest = null) {
    // Find the neighbor connection
    const neighbor = fromNode.neighbors.find(n => n.node === toNode);
    if (!neighbor) return null;

    // Determine transition type based on height difference
    const heightDiff = toNode.y - fromNode.y;
    let type = 'walk';
    if (Math.abs(heightDiff) > 1.0) {
      type = heightDiff > 0 ? 'jump' : 'fall';
    }

    // Calculate transition points
    // Find the overlap/adjacency region between nodes
    const overlapMinX = Math.max(fromNode.minX, toNode.minX);
    const overlapMaxX = Math.min(fromNode.maxX, toNode.maxX);
    const overlapMinZ = Math.max(fromNode.minZ, toNode.minZ);
    const overlapMaxZ = Math.min(fromNode.maxZ, toNode.maxZ);

    // Check if nodes overlap
    const overlapsXZ = overlapMinX < overlapMaxX && overlapMinZ < overlapMaxZ;

    let exitPoint, landingPoint;

    if (overlapsXZ) {
      // Overlapping surfaces (vertical transition)
      // Find optimal transition point that minimizes total path length
      const result = this._findOptimalVerticalTransition(
        fromNode, toNode, overlapMinX, overlapMaxX, overlapMinZ, overlapMaxZ,
        currentPos, finalDest
      );
      exitPoint = result.exitPoint;
      landingPoint = result.landingPoint;

      // Fallback for narrow surfaces: use center of overlap region
      if (!exitPoint || !landingPoint) {
        const centerX = (overlapMinX + overlapMaxX) / 2;
        const centerZ = (overlapMinZ + overlapMaxZ) / 2;
        Debug.log('PathFinder', '_getTransitionInfo: Using overlap center fallback for narrow surface',
                    'center=(' + centerX.toFixed(1) + ',' + centerZ.toFixed(1) + ')');
        exitPoint = { x: centerX, z: centerZ };
        landingPoint = { x: centerX, z: centerZ };
      }
    } else {
      // Adjacent surfaces (edge transition)
      // Find walkable edge points optimized for travel direction
      const result = this._findOptimalEdgeTransition(fromNode, toNode, currentPos, finalDest);
      exitPoint = result.exitPoint;
      landingPoint = result.landingPoint;

      // For edge transitions, use fallback if grid search failed
      // (edge transitions have different geometry constraints)
      if (!exitPoint || !landingPoint) {
        // For vertical transitions, need shared X,Z point
        if (Math.abs(heightDiff) > 1.0) {
          const sharedPoint = this._findVerticalEdgeTransitionPoint(fromNode, toNode);
          exitPoint = exitPoint || sharedPoint;
          landingPoint = landingPoint || { x: sharedPoint.x, z: sharedPoint.z };
        } else {
          // Walk transitions can have different edge points
          if (!exitPoint) {
            exitPoint = this._findEdgePointFallback(fromNode, toNode);
          }
          if (!landingPoint) {
            landingPoint = this._findEdgePointFallback(toNode, fromNode);
          }
        }
      }
    }

    return {
      type,
      exitPoint,
      landingPoint
    };
  }

  /**
   * Find a transition point that is actually reachable from the current position
   * by testing multiple transition candidates and verifying grid paths exist
   * @private
   */
  _findReachableTransition(fromNode, toNode, currentPos, finalDest) {
    // Get all potential transition points by testing edges
    const heightDiff = toNode.y - fromNode.y;
    const overlapMinX = Math.max(fromNode.minX, toNode.minX);
    const overlapMaxX = Math.min(fromNode.maxX, toNode.maxX);
    const overlapMinZ = Math.max(fromNode.minZ, toNode.minZ);
    const overlapMaxZ = Math.min(fromNode.maxZ, toNode.maxZ);

    const overlapsXZ = overlapMinX < overlapMaxX && overlapMinZ < overlapMaxZ;
    if (!overlapsXZ) {
      // Adjacent surfaces - use edge transitions which have different logic
      return null;
    }

    // Test multiple candidate transition points along the edges of the overlap
    const EDGE_MARGIN = 0.6;
    const STEP = 1.0;
    const candidates = [];

    // Generate candidates along all four edges of the overlap
    // West edge (minX)
    for (let z = overlapMinZ + EDGE_MARGIN; z <= overlapMaxZ - EDGE_MARGIN; z += STEP) {
      candidates.push({ x: overlapMinX + EDGE_MARGIN, z });
    }
    // East edge (maxX)
    for (let z = overlapMinZ + EDGE_MARGIN; z <= overlapMaxZ - EDGE_MARGIN; z += STEP) {
      candidates.push({ x: overlapMaxX - EDGE_MARGIN, z });
    }
    // South edge (minZ)
    for (let x = overlapMinX + EDGE_MARGIN; x <= overlapMaxX - EDGE_MARGIN; x += STEP) {
      candidates.push({ x, z: overlapMinZ + EDGE_MARGIN });
    }
    // North edge (maxZ)
    for (let x = overlapMinX + EDGE_MARGIN; x <= overlapMaxX - EDGE_MARGIN; x += STEP) {
      candidates.push({ x, z: overlapMaxZ - EDGE_MARGIN });
    }

    // Sort candidates by distance to final destination (prefer closer ones)
    const targetX = finalDest ? finalDest.x : toNode.x;
    const targetZ = finalDest ? finalDest.z : toNode.z;
    candidates.sort((a, b) => {
      const distA = Math.sqrt(Math.pow(a.x - targetX, 2) + Math.pow(a.z - targetZ, 2));
      const distB = Math.sqrt(Math.pow(b.x - targetX, 2) + Math.pow(b.z - targetZ, 2));
      return distA - distB;
    });

    // Test each candidate for reachability
    for (const candidate of candidates) {
      // Check if this transition point is valid (no obstacles blocking)
      const lowerY = Math.min(fromNode.y, toNode.y);
      const upperY = Math.max(fromNode.y, toNode.y);

      // Verify the transition point itself is walkable
      const sourceWalkable = this._isPointPhysicallyWalkableOnNode(fromNode, candidate.x, candidate.z);
      const destWalkable = this._isPointPhysicallyWalkableOnNode(toNode, candidate.x, candidate.z);
      const transitionClear = !this._hasObstacleBetweenLevels(candidate.x, candidate.z, lowerY, upperY);

      if (!sourceWalkable || !destWalkable || !transitionClear) {
        continue; // This transition point is blocked
      }

      // Try to find a grid path from current position to this candidate
      const surfacePath = this._pathWithinSurface(fromNode, currentPos, candidate);
      if (surfacePath && surfacePath.length > 0) {
        // Found a reachable transition!
        let type = 'walk';
        if (Math.abs(heightDiff) > 1.0) {
          type = heightDiff > 0 ? 'jump' : 'fall';
        }

        return {
          transitionInfo: {
            type,
            exitPoint: candidate,
            landingPoint: candidate
          },
          surfacePath
        };
      }
    }

    return null; // No reachable transition found
  }

  /**
   * Find the closest approach to any transition point when direct path is blocked
   * Returns the best partial path that gets closest to a valid transition
   * @private
   */
  _findClosestApproachToTransition(fromNode, toNode, currentPos, finalDest) {
    if (!fromNode.hasGrid()) return null;

    const heightDiff = toNode.y - fromNode.y;
    const overlapMinX = Math.max(fromNode.minX, toNode.minX);
    const overlapMaxX = Math.min(fromNode.maxX, toNode.maxX);
    const overlapMinZ = Math.max(fromNode.minZ, toNode.minZ);
    const overlapMaxZ = Math.min(fromNode.maxZ, toNode.maxZ);

    // Find all walkable cells on fromNode's grid
    const grid = fromNode.grid;
    const startCell = grid.worldToCell(currentPos.x, currentPos.z);

    // BFS to find the reachable cell closest to the overlap region
    const visited = new Set();
    const queue = [{ cell: startCell, dist: 0 }];
    visited.add(`${startCell.x},${startCell.z}`);

    let bestCell = null;
    let bestDistToOverlap = Infinity;
    const MAX_ITERATIONS = 500;
    let iterations = 0;

    while (queue.length > 0 && iterations < MAX_ITERATIONS) {
      iterations++;
      const { cell } = queue.shift();
      const worldPos = grid.cellToWorld(cell.x, cell.z);

      // Check distance to overlap region
      const clampedX = Math.max(overlapMinX, Math.min(overlapMaxX, worldPos.x));
      const clampedZ = Math.max(overlapMinZ, Math.min(overlapMaxZ, worldPos.z));
      const distToOverlap = Math.sqrt(
        Math.pow(worldPos.x - clampedX, 2) + Math.pow(worldPos.z - clampedZ, 2)
      );

      if (distToOverlap < bestDistToOverlap) {
        bestDistToOverlap = distToOverlap;
        bestCell = cell;
      }

      // Explore neighbors
      const neighbors = grid.getNeighbors(cell.x, cell.z);
      for (const neighbor of neighbors) {
        const key = `${neighbor.x},${neighbor.z}`;
        if (!visited.has(key)) {
          visited.add(key);
          queue.push({ cell: { x: neighbor.x, z: neighbor.z } });
        }
      }
    }

    if (!bestCell) return null;

    // Get path to best cell
    const bestWorldPos = grid.cellToWorld(bestCell.x, bestCell.z);
    const surfacePath = this._pathWithinSurface(fromNode, currentPos, bestWorldPos);

    if (!surfacePath || surfacePath.length === 0) return null;

    // Use this as the exit point - the transition will happen from here
    let type = 'walk';
    if (Math.abs(heightDiff) > 1.0) {
      type = heightDiff > 0 ? 'jump' : 'fall';
    }

    // For landing, find the closest point in the overlap
    const landingX = Math.max(overlapMinX + 0.5, Math.min(overlapMaxX - 0.5, bestWorldPos.x));
    const landingZ = Math.max(overlapMinZ + 0.5, Math.min(overlapMaxZ - 0.5, bestWorldPos.z));

    return {
      transitionInfo: {
        type,
        exitPoint: bestWorldPos,
        landingPoint: { x: landingX, z: landingZ }
      },
      surfacePath
    };
  }

  /**
   * Find optimal transition point for vertical movement between overlapping surfaces
   *
   * SIMPLIFIED APPROACH: Pick the edge that's in the direction of the final destination.
   * Don't iterate through all points - just find the best edge and use a point on it.
   *
   * @private
   */
  _findOptimalVerticalTransition(fromNode, toNode, overlapMinX, overlapMaxX, overlapMinZ, overlapMaxZ, currentPos, finalDest) {
    const heightDiff = Math.abs(toNode.y - fromNode.y);
    const goingUp = toNode.y > fromNode.y;
    const baseCellSize = fromNode.hasGrid() ? fromNode.grid.cellSize : 0.5;

    // For small height differences (step height), use simple transition
    if (heightDiff <= 1.0) {
      return this._findSimpleTransition(fromNode, toNode, overlapMinX, overlapMaxX, overlapMinZ, overlapMaxZ, currentPos, finalDest, baseCellSize);
    }

    // For larger heights, we need to find the EDGE of the upper surface
    const upperNode = goingUp ? toNode : fromNode;
    const lowerNode = goingUp ? fromNode : toNode;
    const EDGE_MARGIN = 0.6;

    // Determine the direction to the final destination
    const targetX = finalDest ? finalDest.x : toNode.x;
    const targetZ = finalDest ? finalDest.z : toNode.z;

    // Determine which edge to prefer based on which edge is CLOSEST to the target
    // This is simpler and more reliable than normal-based selection
    const edges = [
      { axis: 'x', value: upperNode.minX, dir: -1, name: 'west' },
      { axis: 'x', value: upperNode.maxX, dir: 1, name: 'east' },
      { axis: 'z', value: upperNode.minZ, dir: -1, name: 'south' },
      { axis: 'z', value: upperNode.maxZ, dir: 1, name: 'north' }
    ];

    // Find the edge whose center point is closest to the target
    let preferredEdge = null;
    let minDist = Infinity;

    for (const edge of edges) {
      let edgeCenterX, edgeCenterZ;
      if (edge.axis === 'x') {
        edgeCenterX = edge.value;
        edgeCenterZ = (upperNode.minZ + upperNode.maxZ) / 2;
      } else {
        edgeCenterX = (upperNode.minX + upperNode.maxX) / 2;
        edgeCenterZ = edge.value;
      }
      const dist = Math.sqrt(Math.pow(edgeCenterX - targetX, 2) + Math.pow(edgeCenterZ - targetZ, 2));
      if (dist < minDist) {
        minDist = dist;
        preferredEdge = edge;
      }
    }

    // Adaptive step size: use coarser steps for large overlaps to prevent blocking
    const overlapWidth = overlapMaxX - overlapMinX;
    const overlapDepth = overlapMaxZ - overlapMinZ;
    const maxDim = Math.max(overlapWidth, overlapDepth);
    // Ensure we don't check more than ~50 points per edge
    const cellSize = maxDim > 25 ? Math.max(baseCellSize, maxDim / 50) : baseCellSize;

    // Now find a valid transition point on the preferred edge
    const validTransitions = [];
    let iterations = 0;
    const MAX_ITERATIONS = 200; // Limit total iterations across all searches

    if (preferredEdge.axis === 'x') {
      const edgeX = preferredEdge.value;

      // Check if edge is accessible (within overlap)
      if (edgeX >= overlapMinX - 0.5 && edgeX <= overlapMaxX + 0.5) {
        // Find the Z coordinate closest to target
        const idealZ = Math.max(overlapMinZ, Math.min(overlapMaxZ, targetZ));

        // Try points around the ideal Z
        for (let z = overlapMinZ; z <= overlapMaxZ && iterations < MAX_ITERATIONS; z += cellSize) {
          iterations++;
          const outsideX = edgeX + preferredEdge.dir * EDGE_MARGIN;
          const insideX = edgeX - preferredEdge.dir * EDGE_MARGIN;

          const sourcePoint = goingUp
            ? { x: outsideX, z: z }
            : { x: insideX, z: z };
          const destPoint = goingUp
            ? { x: insideX, z: z }
            : { x: outsideX, z: z };

          // Validate landing point is within destination surface
          const landingSurface = goingUp ? upperNode : lowerNode;
          if (destPoint.x < landingSurface.minX - 0.1 || destPoint.x > landingSurface.maxX + 0.1 ||
              z < landingSurface.minZ - 0.1 || z > landingSurface.maxZ + 0.1) {
            continue;
          }

          // Use lenient physical walkability for transitions (ignores edge margins on small surfaces)
          const sourceWalkable = this._isPointPhysicallyWalkableOnNode(fromNode, sourcePoint.x, sourcePoint.z);
          const destWalkable = this._isPointPhysicallyWalkableOnNode(toNode, destPoint.x, destPoint.z);
          const transitionClear = !this._hasObstacleBetweenLevels(edgeX, z, lowerNode.y, upperNode.y);

          // For step-up, also check headroom at exit point for the full step motion
          const stepUpClear = !goingUp || !this._hasStepUpHeadroomBlock(sourcePoint.x, sourcePoint.z, lowerNode.y, upperNode.y);

          if (sourceWalkable && destWalkable && transitionClear && stepUpClear) {
            const distFromIdeal = Math.abs(z - idealZ);
            validTransitions.push({ exitPoint: sourcePoint, landingPoint: destPoint, dist: distFromIdeal });
          }
        }
      }
    } else {
      const edgeZ = preferredEdge.value;

      if (edgeZ >= overlapMinZ - 0.5 && edgeZ <= overlapMaxZ + 0.5) {
        const idealX = Math.max(overlapMinX, Math.min(overlapMaxX, targetX));

        for (let x = overlapMinX; x <= overlapMaxX && iterations < MAX_ITERATIONS; x += cellSize) {
          iterations++;
          const outsideZ = edgeZ + preferredEdge.dir * EDGE_MARGIN;
          const insideZ = edgeZ - preferredEdge.dir * EDGE_MARGIN;

          const sourcePoint = goingUp
            ? { x: x, z: outsideZ }
            : { x: x, z: insideZ };
          const destPoint = goingUp
            ? { x: x, z: insideZ }
            : { x: x, z: outsideZ };

          const landingSurface = goingUp ? upperNode : lowerNode;
          if (x < landingSurface.minX - 0.1 || x > landingSurface.maxX + 0.1 ||
              destPoint.z < landingSurface.minZ - 0.1 || destPoint.z > landingSurface.maxZ + 0.1) {
            continue;
          }

          // Use lenient physical walkability for transitions (ignores edge margins on small surfaces)
          const sourceWalkable = this._isPointPhysicallyWalkableOnNode(fromNode, sourcePoint.x, sourcePoint.z);
          const destWalkable = this._isPointPhysicallyWalkableOnNode(toNode, destPoint.x, destPoint.z);
          const transitionClear = !this._hasObstacleBetweenLevels(x, edgeZ, lowerNode.y, upperNode.y);

          // For step-up, also check headroom at exit point for the full step motion
          const stepUpClear = !goingUp || !this._hasStepUpHeadroomBlock(sourcePoint.x, sourcePoint.z, lowerNode.y, upperNode.y);

          if (sourceWalkable && destWalkable && transitionClear && stepUpClear) {
            const distFromIdeal = Math.abs(x - idealX);
            validTransitions.push({ exitPoint: sourcePoint, landingPoint: destPoint, dist: distFromIdeal });
          }
        }
      }
    }

    // If we found valid transitions on the preferred edge, use the best one
    if (validTransitions.length > 0) {
      validTransitions.sort((a, b) => a.dist - b.dist);
      return validTransitions[0];
    }

    // If preferred edge didn't work, try ALL edges and pick closest to target
    // Use coarser step to prevent blocking
    const allEdgesCellSize = Math.max(cellSize, 1.0);
    const allTransitions = [];
    iterations = 0;

    for (const edge of edges) {
      if (iterations >= MAX_ITERATIONS) break;

      if (edge.axis === 'x') {
        const edgeX = edge.value;
        if (edgeX < overlapMinX - 0.5 || edgeX > overlapMaxX + 0.5) continue;

        for (let z = overlapMinZ; z <= overlapMaxZ && iterations < MAX_ITERATIONS; z += allEdgesCellSize) {
          iterations++;
          const outsideX = edgeX + edge.dir * EDGE_MARGIN;
          const insideX = edgeX - edge.dir * EDGE_MARGIN;

          const sourcePoint = goingUp ? { x: outsideX, z } : { x: insideX, z };
          const destPoint = goingUp ? { x: insideX, z } : { x: outsideX, z };

          const landingSurface = goingUp ? upperNode : lowerNode;
          if (destPoint.x < landingSurface.minX - 0.1 || destPoint.x > landingSurface.maxX + 0.1 ||
              z < landingSurface.minZ - 0.1 || z > landingSurface.maxZ + 0.1) continue;

          // Use lenient physical walkability for transitions
          const sourceWalkable = this._isPointPhysicallyWalkableOnNode(fromNode, sourcePoint.x, sourcePoint.z);
          const destWalkable = this._isPointPhysicallyWalkableOnNode(toNode, destPoint.x, destPoint.z);
          const transitionClear = !this._hasObstacleBetweenLevels(edgeX, z, lowerNode.y, upperNode.y);

          // For step-up, also check headroom at exit point for the full step motion
          const stepUpClear = !goingUp || !this._hasStepUpHeadroomBlock(sourcePoint.x, sourcePoint.z, lowerNode.y, upperNode.y);

          if (sourceWalkable && destWalkable && transitionClear && stepUpClear) {
            const distToTarget = Math.sqrt(Math.pow(destPoint.x - targetX, 2) + Math.pow(destPoint.z - targetZ, 2));
            allTransitions.push({ exitPoint: sourcePoint, landingPoint: destPoint, score: distToTarget });
          }
        }
      } else {
        const edgeZ = edge.value;
        if (edgeZ < overlapMinZ - 0.5 || edgeZ > overlapMaxZ + 0.5) continue;

        for (let x = overlapMinX; x <= overlapMaxX && iterations < MAX_ITERATIONS; x += allEdgesCellSize) {
          iterations++;
          const outsideZ = edgeZ + edge.dir * EDGE_MARGIN;
          const insideZ = edgeZ - edge.dir * EDGE_MARGIN;

          const sourcePoint = goingUp ? { x, z: outsideZ } : { x, z: insideZ };
          const destPoint = goingUp ? { x, z: insideZ } : { x, z: outsideZ };

          const landingSurface = goingUp ? upperNode : lowerNode;
          if (x < landingSurface.minX - 0.1 || x > landingSurface.maxX + 0.1 ||
              destPoint.z < landingSurface.minZ - 0.1 || destPoint.z > landingSurface.maxZ + 0.1) continue;

          // Use lenient physical walkability for transitions
          const sourceWalkable = this._isPointPhysicallyWalkableOnNode(fromNode, sourcePoint.x, sourcePoint.z);
          const destWalkable = this._isPointPhysicallyWalkableOnNode(toNode, destPoint.x, destPoint.z);
          const transitionClear = !this._hasObstacleBetweenLevels(x, edgeZ, lowerNode.y, upperNode.y);

          // For step-up, also check headroom at exit point for the full step motion
          const stepUpClear = !goingUp || !this._hasStepUpHeadroomBlock(sourcePoint.x, sourcePoint.z, lowerNode.y, upperNode.y);

          if (sourceWalkable && destWalkable && transitionClear && stepUpClear) {
            const distToTarget = Math.sqrt(Math.pow(destPoint.x - targetX, 2) + Math.pow(destPoint.z - targetZ, 2));
            allTransitions.push({ exitPoint: sourcePoint, landingPoint: destPoint, score: distToTarget });
          }
        }
      }
    }

    if (allTransitions.length > 0) {
      allTransitions.sort((a, b) => a.score - b.score);
      return allTransitions[0];
    }

    // Ultimate fallback - try simple transition, but return null if completely blocked
    return this._findSimpleTransition(fromNode, toNode, overlapMinX, overlapMaxX, overlapMinZ, overlapMaxZ, currentPos, finalDest, cellSize);
  }

  /**
   * Simple transition finder for low height differences
   * Prioritizes points closer to the final destination
   * @private
   */
  _findSimpleTransition(fromNode, toNode, overlapMinX, overlapMaxX, overlapMinZ, overlapMaxZ, currentPos, finalDest, cellSize) {
    const EDGE_CLEARANCE = 0.5;
    const safeMinX = overlapMinX + EDGE_CLEARANCE;
    const safeMaxX = overlapMaxX - EDGE_CLEARANCE;
    const safeMinZ = overlapMinZ + EDGE_CLEARANCE;
    const safeMaxZ = overlapMaxZ - EDGE_CLEARANCE;

    // Target is the final destination, or fall back to destination node center
    const targetX = finalDest ? finalDest.x : toNode.x;
    const targetZ = finalDest ? finalDest.z : toNode.z;

    const validTransitions = [];

    // If safe bounds are invalid or too narrow (less than cellSize), use the full overlap
    // This is important for narrow step-up platforms where edge clearance would reduce
    // the search to a single point that might be blocked by ceilings
    const safeRangeX = safeMaxX - safeMinX;
    const safeRangeZ = safeMaxZ - safeMinZ;
    const minSearchRange = cellSize * 2; // Need at least 2 cells worth of search space

    const actualMinX = (safeMinX < safeMaxX && safeRangeX >= minSearchRange) ? safeMinX : overlapMinX + 0.1;
    const actualMaxX = (safeMinX < safeMaxX && safeRangeX >= minSearchRange) ? safeMaxX : overlapMaxX - 0.1;
    const actualMinZ = (safeMinZ < safeMaxZ && safeRangeZ >= minSearchRange) ? safeMinZ : overlapMinZ + 0.1;
    const actualMaxZ = (safeMinZ < safeMaxZ && safeRangeZ >= minSearchRange) ? safeMaxZ : overlapMaxZ - 0.1;

    // Get height info for wall detection
    const lowerY = Math.min(fromNode.y, toNode.y);
    const upperY = Math.max(fromNode.y, toNode.y);
    const heightDiff = Math.abs(fromNode.y - toNode.y);
    const goingUp = toNode.y > fromNode.y;

    // Adaptive step size for large overlaps to prevent blocking
    const overlapWidth = actualMaxX - actualMinX;
    const overlapDepth = actualMaxZ - actualMinZ;
    const maxDim = Math.max(overlapWidth, overlapDepth);
    // Use coarser step for large regions (limit to ~100 total points)
    const adaptiveStep = maxDim > 5 ? Math.max(cellSize, Math.sqrt(overlapWidth * overlapDepth) / 10) : cellSize;

    let iterations = 0;
    const MAX_ITERATIONS = 150;

    for (let x = actualMinX; x <= actualMaxX && iterations < MAX_ITERATIONS; x += adaptiveStep) {
      for (let z = actualMinZ; z <= actualMaxZ && iterations < MAX_ITERATIONS; z += adaptiveStep) {
        iterations++;
        // Use lenient physical walkability check for transitions (ignores edge margins)
        const sourceWalkable = this._isPointPhysicallyWalkableOnNode(fromNode, x, z);
        const destWalkable = this._isPointPhysicallyWalkableOnNode(toNode, x, z);

        if (sourceWalkable && destWalkable) {
          // For height differences, check if there's a wall blocking the transition
          if (heightDiff > 0.5 && this._hasObstacleBetweenLevels(x, z, lowerY, upperY)) {
            continue; // Wall blocks this transition point
          }

          // For step-up transitions, check that there's headroom for the step-up at the exit point.
          // A ceiling at the lower surface's headroom limit can block step-up even if standing is allowed.
          if (goingUp && this._hasStepUpHeadroomBlock(x, z, lowerY, upperY)) {
            continue; // Step-up headroom blocked
          }

          // Score ONLY by distance to target - this is critical for correct direction
          const distToTarget = Math.sqrt(Math.pow(x - targetX, 2) + Math.pow(z - targetZ, 2));
          validTransitions.push({ x, z, score: distToTarget });
        }
      }
    }

    if (validTransitions.length > 0) {
      validTransitions.sort((a, b) => a.score - b.score);
      const best = validTransitions[0];
      return {
        exitPoint: { x: best.x, z: best.z },
        landingPoint: { x: best.x, z: best.z }
      };
    }

    // No valid transitions found - all points are blocked
    // Return null to signal that this transition is completely blocked,
    // allowing the caller to try alternative routes or fallback handlers
    Debug.log('PathFinder', '_findSimpleTransition: No valid transition points found in overlap region',
                '(all points blocked). Overlap:', overlapMinX.toFixed(1), '-', overlapMaxX.toFixed(1),
                'x', overlapMinZ.toFixed(1), '-', overlapMaxZ.toFixed(1));
    return { exitPoint: null, landingPoint: null };
  }

  /**
   * Check if there's an obstacle between two height levels at a given XZ position
   * This detects walls that would block vertical transitions
   * @private
   */
  _hasObstacleBetweenLevels(x, z, y1, y2) {
    if (!this.collisionSystem) return false;

    const lowerY = Math.min(y1, y2);
    const upperY = Math.max(y1, y2);
    const halfWidth = 0.45;

    const queryBox = {
      minX: x - halfWidth,
      maxX: x + halfWidth,
      minY: lowerY + 0.1,
      maxY: upperY + 2.0,
      minZ: z - halfWidth,
      maxZ: z + halfWidth
    };

    const obstacles = this.collisionSystem.queryCuboids(queryBox);

    for (const obstacle of obstacles) {
      const obsBottom = obstacle.y;
      const obsTop = obstacle.y + obstacle.height;
      const obsMinX = obstacle.x;
      const obsMaxX = obstacle.x + obstacle.width;
      const obsMinZ = obstacle.z;
      const obsMaxZ = obstacle.z + obstacle.depth;

      // CRITICAL: Verify actual XZ overlap with query box.
      // The spatial index returns candidates based on cell proximity, not exact intersection.
      // Without this check, obstacles in adjacent cells (e.g., ceiling at Z=-1) would
      // incorrectly block positions that don't actually overlap (e.g., checking at Z=-2).
      if (queryBox.minX >= obsMaxX || queryBox.maxX <= obsMinX ||
          queryBox.minZ >= obsMaxZ || queryBox.maxZ <= obsMinZ) {
        continue; // No XZ overlap - skip this obstacle
      }

      // An obstacle blocks the transition if it:
      // 1. Extends from the lower surface upward (it's a wall)
      // 2. Is tall enough to block climbing (height > 1.0)

      // Skip if this is the floor we're standing on (obstacle top IS the lower surface)
      if (Math.abs(obsTop - lowerY) < 0.1) {
        continue;
      }

      // Skip if this is the destination surface we're transitioning TO
      // (obstacle top IS the upper surface - this is our target, not a blocker!)
      if (Math.abs(obsTop - upperY) < 0.1) {
        continue;
      }

      // Check if it's a wall: sits on/near lower surface and extends upward
      const sitsOnLower = obsBottom <= lowerY + 0.5;
      const extendsUp = obsTop > lowerY + 1.0;

      if (sitsOnLower && extendsUp) {
        // This is a wall that blocks the transition
        Debug.log('PathFinder', 'WALL detected - obstacle at', obstacle.x.toFixed(1), obstacle.y.toFixed(1), obstacle.z.toFixed(1),
                    'size', obstacle.width.toFixed(1), 'x', obstacle.height.toFixed(1), 'x', obstacle.depth.toFixed(1),
                    '| obsBottom:', obsBottom.toFixed(1), 'obsTop:', obsTop.toFixed(1),
                    '| lowerY:', lowerY.toFixed(1), 'upperY:', upperY.toFixed(1));
        return true;
      }

      // Also check for ceilings (obstacles above that block upward movement)
      if (obsBottom > lowerY + 0.5 && obsBottom < upperY + 2.0 && obsTop > lowerY + 0.5) {
        // Skip if this obstacle's top IS the destination surface (it's part of the staircase)
        if (Math.abs(obsTop - upperY) < 0.3) {
          continue;
        }
        // Skip obstacles sitting ON the destination surface - they're the NEXT level up,
        // not blockers for THIS transition. HOWEVER, they still block if they extend into
        // the head space of someone standing on the LOWER surface.
        // This handles L-shaped stacked structures where the upper cuboid extends beyond
        // the lower one - those extended portions ARE walls from the lower surface.
        if (Math.abs(obsBottom - upperY) < 0.3) {
          // Character at lowerY needs clearance from lowerY to lowerY + CHARACTER_HEIGHT (2.0)
          // Only skip if the obstacle is entirely above the character's head space
          const CHARACTER_HEIGHT = 2.0;
          if (obsBottom >= lowerY + CHARACTER_HEIGHT) {
            continue; // Obstacle is above head space - safe to skip
          }
          // Otherwise, obstacle enters head space and IS a blocker even though it
          // sits on the destination surface. This is a wall from the lower level.
          Debug.log('PathFinder', 'L-SHAPED WALL detected - obstacle sits on destination but blocks lower level:',
                      obstacle.x.toFixed(1), obstacle.y.toFixed(1), obstacle.z.toFixed(1),
                      'size', obstacle.width.toFixed(1), 'x', obstacle.height.toFixed(1), 'x', obstacle.depth.toFixed(1),
                      '| obsBottom:', obsBottom.toFixed(1), 'obsTop:', obsTop.toFixed(1),
                      '| lowerY:', lowerY.toFixed(1), 'upperY:', upperY.toFixed(1));
          return true;
        }
        // Obstacle blocks this transition point
        Debug.log('PathFinder', 'CEILING detected - obstacle at', obstacle.x.toFixed(1), obstacle.y.toFixed(1), obstacle.z.toFixed(1),
                    'size', obstacle.width.toFixed(1), 'x', obstacle.height.toFixed(1), 'x', obstacle.depth.toFixed(1),
                    '| obsBottom:', obsBottom.toFixed(1), 'obsTop:', obsTop.toFixed(1),
                    '| lowerY:', lowerY.toFixed(1), 'upperY:', upperY.toFixed(1));
        return true;
      }
    }

    return false;
  }

  /**
   * Check if a transition between two adjacent surfaces is blocked by a wall
   * This handles the case where a wall sits on the lower surface blocking access to the higher one
   * @private
   */
  _isTransitionBlocked(exitPoint, landingPoint, fromY, toY) {
    if (!this.collisionSystem) return false;

    const minY = Math.min(fromY, toY);
    const maxY = Math.max(fromY, toY) + 2.0; // Character height

    // Check the horizontal space between exit and landing points
    const midX = (exitPoint.x + landingPoint.x) / 2;
    const midZ = (exitPoint.z + landingPoint.z) / 2;
    const halfWidth = 0.5;

    // Query for obstacles in the transition zone
    const queryBox = {
      minX: Math.min(exitPoint.x, landingPoint.x) - halfWidth,
      maxX: Math.max(exitPoint.x, landingPoint.x) + halfWidth,
      minY: minY + 0.1,
      maxY: maxY,
      minZ: Math.min(exitPoint.z, landingPoint.z) - halfWidth,
      maxZ: Math.max(exitPoint.z, landingPoint.z) + halfWidth
    };

    const obstacles = this.collisionSystem.queryCuboids(queryBox);

    for (const obstacle of obstacles) {
      const obsBottom = obstacle.y;
      const obsTop = obstacle.y + obstacle.height;

      // Skip floor surfaces
      if (Math.abs(obsTop - fromY) < 0.1 || Math.abs(obsTop - toY) < 0.1) continue;

      // Check if this is a blocking wall
      // A wall blocks if it sits on the lower surface and extends up
      if (obsBottom <= minY + 0.5 && obsTop > minY + 1.0) {
        // Check if wall is in the transition path
        const obsMinX = obstacle.x;
        const obsMaxX = obstacle.x + obstacle.width;
        const obsMinZ = obstacle.z;
        const obsMaxZ = obstacle.z + obstacle.depth;

        // Check if the transition line intersects this wall
        if (midX >= obsMinX - halfWidth && midX <= obsMaxX + halfWidth &&
            midZ >= obsMinZ - halfWidth && midZ <= obsMaxZ + halfWidth) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Generate points in a spiral pattern around a center point
   * @private
   */
  _getSpiralPoints(centerX, centerZ, radius, step, minX, maxX, minZ, maxZ) {
    const points = [];
    const numPoints = Math.max(4, Math.ceil(2 * Math.PI * radius / step));

    for (let i = 0; i < numPoints; i++) {
      const angle = (2 * Math.PI * i) / numPoints;
      const x = centerX + radius * Math.cos(angle);
      const z = centerZ + radius * Math.sin(angle);

      // Only include points within bounds
      if (x >= minX && x <= maxX && z >= minZ && z <= maxZ) {
        points.push({ x, z });
      }
    }

    return points;
  }

  /**
   * Check if a point is walkable on a node's grid
   * @private
   */
  _isPointWalkableOnNode(node, x, z) {
    if (!node.hasGrid()) {
      return node.containsPoint(x, z);
    }
    const cell = node.grid.worldToCell(x, z);
    return cell && node.grid.isWalkable(cell.x, cell.z);
  }

  /**
   * Check if a point is physically walkable on a node (no obstacles blocking)
   * This is more lenient than _isPointWalkableOnNode - it ignores edge margins
   * Used for staircase transitions where edge margins shouldn't apply
   * @private
   */
  _isPointPhysicallyWalkableOnNode(node, x, z) {
    // Must be within surface bounds
    if (!node.containsPoint(x, z)) {
      Debug.log('PathFinder', 'Point', x.toFixed(1), z.toFixed(1), 'not contained in node',
                  'bounds:', node.minX, '-', node.maxX, ',', node.minZ, '-', node.maxZ);
      return false;
    }

    // If no collision system, assume walkable if within bounds
    if (!this.collisionSystem) {
      return true;
    }

    // Check for actual obstacles at this point (character-sized probe)
    const CHARACTER_WIDTH = 0.9;
    const CHARACTER_HEIGHT = 2.0;
    const CHARACTER_DEPTH = 0.9;
    const halfWidth = CHARACTER_WIDTH / 2;
    const halfDepth = CHARACTER_DEPTH / 2;

    const queryBox = {
      minX: x - halfWidth,
      maxX: x + halfWidth,
      minY: node.y + 0.01,
      maxY: node.y + CHARACTER_HEIGHT,
      minZ: z - halfDepth,
      maxZ: z + halfDepth
    };

    const obstacles = this.collisionSystem.queryCuboids(queryBox);

    for (const obstacle of obstacles) {
      const obsTop = obstacle.y + obstacle.height;
      const obsBottom = obstacle.y;

      // Skip if this is the floor we're standing on (obstacle top IS our surface)
      if (Math.abs(obsTop - node.y) < 0.1) continue;
      if (obsTop <= node.y + 0.01) continue;

      // Skip obstacles that sit ON this surface (their bottom matches our surface Y)
      // These are stacked surfaces forming the next level, not blockers for THIS level
      if (Math.abs(obsBottom - node.y) < 0.3) continue;

      // Check if obstacle occupies head space and blocks passage
      if (obsBottom < node.y + CHARACTER_HEIGHT) {
        const obsMinX = obstacle.x;
        const obsMaxX = obstacle.x + obstacle.width;
        const obsMinZ = obstacle.z;
        const obsMaxZ = obstacle.z + obstacle.depth;

        if (queryBox.minX < obsMaxX && queryBox.maxX > obsMinX &&
            queryBox.minZ < obsMaxZ && queryBox.maxZ > obsMinZ) {
          Debug.log('PathFinder', 'Point', x.toFixed(1), z.toFixed(1), 'on node Y=', node.y,
                      'blocked by obstacle: y=', obsBottom, '-', obsTop,
                      'x=', obsMinX, '-', obsMaxX, 'z=', obsMinZ, '-', obsMaxZ);
          return false; // Blocked by obstacle
        }
      }
    }

    return true; // No obstacles blocking
  }

  /**
   * Check if there's a ceiling/obstacle blocking step-up headroom at a specific XZ position.
   * When stepping UP from lowerY to upperY, the character's head must rise from
   * lowerY + CHARACTER_HEIGHT to upperY + CHARACTER_HEIGHT. This method checks for
   * obstacles that would block this vertical head movement at the EXIT point.
   *
   * This is different from _hasObstacleBetweenLevels which checks the transition PATH.
   * This specifically checks for the "flying buttress" case: a ceiling that allows
   * standing at the lower level but blocks the step-up motion.
   *
   * @param {number} x - X position to check
   * @param {number} z - Z position to check
   * @param {number} lowerY - Lower surface Y
   * @param {number} upperY - Upper surface Y
   * @returns {boolean} True if an obstacle blocks step-up headroom
   * @private
   */
  _hasStepUpHeadroomBlock(x, z, lowerY, upperY) {
    if (!this.collisionSystem) return false;

    const CHARACTER_WIDTH = 0.9;
    const CHARACTER_HEIGHT = 2.0;
    const CHARACTER_DEPTH = 0.9;
    const halfWidth = CHARACTER_WIDTH / 2;
    const halfDepth = CHARACTER_DEPTH / 2;

    // Check the vertical space from current headroom limit to target headroom limit.
    // Standing at lowerY, head is at lowerY + CHARACTER_HEIGHT.
    // After stepping to upperY, head will be at upperY + CHARACTER_HEIGHT.
    // Any obstacle in this range blocks the step-up.
    const headAtLower = lowerY + CHARACTER_HEIGHT;
    const headAtUpper = upperY + CHARACTER_HEIGHT;

    const queryBox = {
      minX: x - halfWidth,
      maxX: x + halfWidth,
      minY: headAtLower - 0.01,  // Just below current head height
      maxY: headAtUpper,         // To target head height
      minZ: z - halfDepth,
      maxZ: z + halfDepth
    };

    const obstacles = this.collisionSystem.queryCuboids(queryBox);

    for (const obstacle of obstacles) {
      const obsBottom = obstacle.y;
      const obsTop = obstacle.y + obstacle.height;

      // Skip if obstacle is entirely below the headroom check range
      if (obsTop <= headAtLower) continue;

      // Skip if obstacle is entirely above the headroom check range
      if (obsBottom >= headAtUpper) continue;

      // Skip if this is the destination surface itself (top = upperY)
      if (Math.abs(obsTop - upperY) < 0.1) continue;

      // Skip if this is a step/platform the character will land on (bottom ≈ upperY, top > upperY)
      // These are valid stepping surfaces, not blockers
      if (Math.abs(obsBottom - upperY) < 0.3) continue;

      // Check XZ overlap
      const obsMinX = obstacle.x;
      const obsMaxX = obstacle.x + obstacle.width;
      const obsMinZ = obstacle.z;
      const obsMaxZ = obstacle.z + obstacle.depth;

      if (queryBox.minX < obsMaxX && queryBox.maxX > obsMinX &&
          queryBox.minZ < obsMaxZ && queryBox.maxZ > obsMinZ) {
        Debug.log('PathFinder', 'Step-up headroom blocked at', x.toFixed(1), z.toFixed(1),
                    'lowerY=', lowerY, 'upperY=', upperY,
                    'obstacle y=', obsBottom.toFixed(1), '-', obsTop.toFixed(1),
                    'headroom range=', headAtLower.toFixed(1), '-', headAtUpper.toFixed(1));
        return true; // Blocked
      }
    }

    return false; // No obstacles blocking step-up headroom
  }

  /**
   * Find optimal edge transition for adjacent (non-overlapping) surfaces
   * For height differences > 1, ensures there's no blocking wall between surfaces
   * @private
   */
  _findOptimalEdgeTransition(fromNode, toNode, currentPos, finalDest) {
    const EDGE_CLEARANCE = 0.5; // Stay this far from surface edges
    const heightDiff = Math.abs(toNode.y - fromNode.y);

    // Determine which edges are adjacent
    const dx = toNode.x - fromNode.x;
    const dz = toNode.z - fromNode.z;

    // TARGET is the final destination - this is what we optimize towards
    const targetX = finalDest ? finalDest.x : toNode.x;
    const targetZ = finalDest ? finalDest.z : toNode.z;

    // Find the overlapping range on the shared edge (with clearance)
    const overlapMinX = Math.max(fromNode.minX, toNode.minX) + EDGE_CLEARANCE;
    const overlapMaxX = Math.min(fromNode.maxX, toNode.maxX) - EDGE_CLEARANCE;
    const overlapMinZ = Math.max(fromNode.minZ, toNode.minZ) + EDGE_CLEARANCE;
    const overlapMaxZ = Math.min(fromNode.maxZ, toNode.maxZ) - EDGE_CLEARANCE;

    const baseCellSize = fromNode.hasGrid() ? fromNode.grid.cellSize : 0.5;
    const validTransitions = [];

    // Adaptive step size: use coarser steps for long edges to prevent blocking
    const edgeLengthX = overlapMaxX - overlapMinX;
    const edgeLengthZ = overlapMaxZ - overlapMinZ;
    const maxEdgeLength = Math.max(edgeLengthX, edgeLengthZ);
    // Limit to ~50 points along the edge
    const cellSize = maxEdgeLength > 25 ? Math.max(baseCellSize, maxEdgeLength / 50) : baseCellSize;

    // For significant height differences, search for unblocked transition points
    if (heightDiff > 1.0 && this.collisionSystem) {
      let iterations = 0;
      const MAX_ITERATIONS = 100;

      // Search along the shared edge for spots without blocking walls
      if (Math.abs(dx) > Math.abs(dz)) {
        // X direction - search along Z
        const fromEdgeX = dx > 0 ? fromNode.maxX - EDGE_CLEARANCE : fromNode.minX + EDGE_CLEARANCE;
        const toEdgeX = dx > 0 ? toNode.minX + EDGE_CLEARANCE : toNode.maxX - EDGE_CLEARANCE;

        for (let z = overlapMinZ; z <= overlapMaxZ && iterations < MAX_ITERATIONS; z += cellSize) {
          iterations++;
          const exitPoint = { x: fromEdgeX, z };
          const landingPoint = { x: toEdgeX, z };

          // Check if there's a wall blocking this transition
          if (!this._isTransitionBlocked(exitPoint, landingPoint, fromNode.y, toNode.y)) {
            // Use lenient physical walkability for transitions
            const sourceWalkable = this._isPointPhysicallyWalkableOnNode(fromNode, exitPoint.x, exitPoint.z);
            const destWalkable = this._isPointPhysicallyWalkableOnNode(toNode, landingPoint.x, landingPoint.z);

            if (sourceWalkable && destWalkable) {
              // Score by distance to TARGET, not current position
              const distToTarget = Math.sqrt(
                Math.pow(landingPoint.x - targetX, 2) + Math.pow(landingPoint.z - targetZ, 2)
              );
              validTransitions.push({ exitPoint, landingPoint, score: distToTarget });
            }
          }
        }
      } else {
        // Z direction - search along X
        const fromEdgeZ = dz > 0 ? fromNode.maxZ - EDGE_CLEARANCE : fromNode.minZ + EDGE_CLEARANCE;
        const toEdgeZ = dz > 0 ? toNode.minZ + EDGE_CLEARANCE : toNode.maxZ - EDGE_CLEARANCE;

        for (let x = overlapMinX; x <= overlapMaxX && iterations < MAX_ITERATIONS; x += cellSize) {
          iterations++;
          const exitPoint = { x, z: fromEdgeZ };
          const landingPoint = { x, z: toEdgeZ };

          if (!this._isTransitionBlocked(exitPoint, landingPoint, fromNode.y, toNode.y)) {
            // Use lenient physical walkability for transitions
            const sourceWalkable = this._isPointPhysicallyWalkableOnNode(fromNode, exitPoint.x, exitPoint.z);
            const destWalkable = this._isPointPhysicallyWalkableOnNode(toNode, landingPoint.x, landingPoint.z);

            if (sourceWalkable && destWalkable) {
              // Score by distance to TARGET, not current position
              const distToTarget = Math.sqrt(
                Math.pow(landingPoint.x - targetX, 2) + Math.pow(landingPoint.z - targetZ, 2)
              );
              validTransitions.push({ exitPoint, landingPoint, score: distToTarget });
            }
          }
        }
      }

      if (validTransitions.length > 0) {
        validTransitions.sort((a, b) => a.score - b.score);
        return validTransitions[0];
      }
    }

    // Standard edge transition - use point closest to target on the shared edge
    let exitPoint, landingPoint;

    // Clamp target to valid range on shared edge
    const clampedTargetZ = Math.max(
      Math.max(fromNode.minZ, toNode.minZ) + EDGE_CLEARANCE,
      Math.min(Math.min(fromNode.maxZ, toNode.maxZ) - EDGE_CLEARANCE, targetZ)
    );
    const clampedTargetX = Math.max(
      Math.max(fromNode.minX, toNode.minX) + EDGE_CLEARANCE,
      Math.min(Math.min(fromNode.maxX, toNode.maxX) - EDGE_CLEARANCE, targetX)
    );

    // For VERTICAL edge transitions (falls/jumps), exit and landing must share X,Z
    // because you drop/jump straight down - landing at the same X,Z you left from
    if (heightDiff > 1.0) {
      // Find the TRUE XZ overlap where both surfaces exist
      const trueOverlapMinX = Math.max(fromNode.minX, toNode.minX);
      const trueOverlapMaxX = Math.min(fromNode.maxX, toNode.maxX);
      const trueOverlapMinZ = Math.max(fromNode.minZ, toNode.minZ);
      const trueOverlapMaxZ = Math.min(fromNode.maxZ, toNode.maxZ);

      // Check if there's actual XZ overlap
      if (trueOverlapMinX < trueOverlapMaxX && trueOverlapMinZ < trueOverlapMaxZ) {
        // There's XZ overlap - find a point within it
        const safeMinX = trueOverlapMinX + EDGE_CLEARANCE;
        const safeMaxX = trueOverlapMaxX - EDGE_CLEARANCE;
        const safeMinZ = trueOverlapMinZ + EDGE_CLEARANCE;
        const safeMaxZ = trueOverlapMaxZ - EDGE_CLEARANCE;

        // Clamp target to safe overlap region
        const transitionX = Math.max(safeMinX, Math.min(safeMaxX, targetX));
        const transitionZ = Math.max(safeMinZ, Math.min(safeMaxZ, targetZ));

        // Use SAME X,Z for both exit and landing
        exitPoint = { x: transitionX, z: transitionZ };
        landingPoint = { x: transitionX, z: transitionZ };

        Debug.log('PathFinder', '_findOptimalEdgeTransition: Vertical edge transition at shared X,Z',
                  transitionX.toFixed(1), transitionZ.toFixed(1),
                  'overlap=[' + trueOverlapMinX.toFixed(1) + '-' + trueOverlapMaxX.toFixed(1) + ',' +
                  trueOverlapMinZ.toFixed(1) + '-' + trueOverlapMaxZ.toFixed(1) + ']');
      } else {
        // No XZ overlap - edge-adjacent surfaces at different heights
        // Character must walk to edge, then fall/jump diagonally
        // Find the closest point on the shared edge that's within both surfaces
        Debug.log('PathFinder', '_findOptimalEdgeTransition: No XZ overlap for vertical transition',
                  'fromNode bounds: X=[' + fromNode.minX.toFixed(1) + ',' + fromNode.maxX.toFixed(1) + ']',
                  'Z=[' + fromNode.minZ.toFixed(1) + ',' + fromNode.maxZ.toFixed(1) + ']',
                  'toNode bounds: X=[' + toNode.minX.toFixed(1) + ',' + toNode.maxX.toFixed(1) + ']',
                  'Z=[' + toNode.minZ.toFixed(1) + ',' + toNode.maxZ.toFixed(1) + ']');

        // For edge-adjacent vertical transitions, use the shared edge line
        if (Math.abs(dx) > Math.abs(dz)) {
          // X-direction: shared edge is a vertical line at the boundary
          const sharedX = dx > 0 ? fromNode.maxX : fromNode.minX;
          // Z must be in the Z overlap (even if X doesn't overlap)
          const zOverlapMin = Math.max(fromNode.minZ, toNode.minZ) + EDGE_CLEARANCE;
          const zOverlapMax = Math.min(fromNode.maxZ, toNode.maxZ) - EDGE_CLEARANCE;
          const transitionZ = Math.max(zOverlapMin, Math.min(zOverlapMax, targetZ));

          // Exit point slightly inside fromNode, landing slightly inside toNode
          exitPoint = { x: sharedX - (dx > 0 ? EDGE_CLEARANCE : -EDGE_CLEARANCE), z: transitionZ };
          // Landing must be ON the destination surface (within its X bounds)
          const landingX = Math.max(toNode.minX + EDGE_CLEARANCE, Math.min(toNode.maxX - EDGE_CLEARANCE, sharedX));
          landingPoint = { x: landingX, z: transitionZ };
        } else {
          // Z-direction: shared edge is a horizontal line at the boundary
          const sharedZ = dz > 0 ? fromNode.maxZ : fromNode.minZ;
          const xOverlapMin = Math.max(fromNode.minX, toNode.minX) + EDGE_CLEARANCE;
          const xOverlapMax = Math.min(fromNode.maxX, toNode.maxX) - EDGE_CLEARANCE;
          const transitionX = Math.max(xOverlapMin, Math.min(xOverlapMax, targetX));

          exitPoint = { x: transitionX, z: sharedZ - (dz > 0 ? EDGE_CLEARANCE : -EDGE_CLEARANCE) };
          const landingZ = Math.max(toNode.minZ + EDGE_CLEARANCE, Math.min(toNode.maxZ - EDGE_CLEARANCE, sharedZ));
          landingPoint = { x: transitionX, z: landingZ };
        }
      }

      // Validate points are within their respective nodes
      if (exitPoint) {
        exitPoint.x = Math.max(fromNode.minX + 0.1, Math.min(fromNode.maxX - 0.1, exitPoint.x));
        exitPoint.z = Math.max(fromNode.minZ + 0.1, Math.min(fromNode.maxZ - 0.1, exitPoint.z));
      }
      if (landingPoint) {
        landingPoint.x = Math.max(toNode.minX + 0.1, Math.min(toNode.maxX - 0.1, landingPoint.x));
        landingPoint.z = Math.max(toNode.minZ + 0.1, Math.min(toNode.maxZ - 0.1, landingPoint.z));
      }

      return { exitPoint, landingPoint };
    }

    // WALK transitions (no significant height change) - can use different edge points
    if (Math.abs(dx) > Math.abs(dz)) {
      const edgeX = dx > 0 ? fromNode.maxX - EDGE_CLEARANCE : fromNode.minX + EDGE_CLEARANCE;
      exitPoint = this._findWalkableNearEdge(fromNode, edgeX, clampedTargetZ, 'x', dx > 0 ? -1 : 1);

      const landingEdgeX = dx > 0 ? toNode.minX + EDGE_CLEARANCE : toNode.maxX - EDGE_CLEARANCE;
      landingPoint = this._findWalkableNearEdge(toNode, landingEdgeX, clampedTargetZ, 'x', dx > 0 ? 1 : -1);
    } else {
      const edgeZ = dz > 0 ? fromNode.maxZ - EDGE_CLEARANCE : fromNode.minZ + EDGE_CLEARANCE;
      exitPoint = this._findWalkableNearEdge(fromNode, clampedTargetX, edgeZ, 'z', dz > 0 ? -1 : 1);

      const landingEdgeZ = dz > 0 ? toNode.minZ + EDGE_CLEARANCE : toNode.maxZ - EDGE_CLEARANCE;
      landingPoint = this._findWalkableNearEdge(toNode, clampedTargetX, landingEdgeZ, 'z', dz > 0 ? 1 : -1);
    }

    // Ensure we never return null - use proper edge fallback, not node centers
    if (!exitPoint) {
      exitPoint = this._findEdgePointFallback(fromNode, toNode);
      Debug.log('PathFinder', '_findOptimalEdgeTransition: Using edge fallback for exit point');
    }
    if (!landingPoint) {
      landingPoint = this._findEdgePointFallback(toNode, fromNode);
      Debug.log('PathFinder', '_findOptimalEdgeTransition: Using edge fallback for landing point');
    }

    return { exitPoint, landingPoint };
  }

  /**
   * Find walkable point near an edge, searching inward if edge is blocked
   * @private
   */
  _findWalkableNearEdge(node, x, z, axis, searchDir) {
    if (!node.hasGrid()) {
      return { x, z };
    }

    const grid = node.grid;
    const cellSize = grid.cellSize;
    const maxSearch = axis === 'x' ? node.width : node.depth;

    for (let offset = 0; offset < maxSearch; offset += cellSize) {
      const testX = axis === 'x' ? x + offset * searchDir : x;
      const testZ = axis === 'z' ? z + offset * searchDir : z;

      // Check bounds
      if (testX < node.minX || testX > node.maxX || testZ < node.minZ || testZ > node.maxZ) {
        break;
      }

      const cell = grid.worldToCell(testX, testZ);
      if (cell && grid.isWalkable(cell.x, cell.z)) {
        const worldPos = grid.cellToWorld(cell.x, cell.z);
        return { x: worldPos.x, z: worldPos.z };
      }
    }

    // No walkable found along edge, try broader search
    return this._findWalkableNearPoint(node, x, z, 5);
  }

  /**
   * Find a walkable point in a region using the node's grid
   * @private
   */
  _findWalkableInRegion(node, minX, maxX, minZ, maxZ) {
    if (!node.hasGrid()) {
      // No grid - return center
      return { x: (minX + maxX) / 2, z: (minZ + maxZ) / 2 };
    }

    const grid = node.grid;

    // Convert region bounds to cell coordinates
    const cellMinX = Math.max(0, Math.floor((minX - grid.minX) / grid.cellSize));
    const cellMaxX = Math.min(grid.width - 1, Math.floor((maxX - grid.minX) / grid.cellSize));
    const cellMinZ = Math.max(0, Math.floor((minZ - grid.minZ) / grid.cellSize));
    const cellMaxZ = Math.min(grid.height - 1, Math.floor((maxZ - grid.minZ) / grid.cellSize));

    // Search for walkable cells in the region, preferring center
    const centerCellX = Math.floor((cellMinX + cellMaxX) / 2);
    const centerCellZ = Math.floor((cellMinZ + cellMaxZ) / 2);

    // Spiral outward from center to find nearest walkable cell
    // Limit radius to prevent blocking on large regions
    const maxRadius = Math.min(50, Math.max(cellMaxX - cellMinX, cellMaxZ - cellMinZ) + 1);
    let iterations = 0;
    const MAX_ITERATIONS = 500;

    for (let radius = 0; radius <= maxRadius && iterations < MAX_ITERATIONS; radius++) {
      for (let dx = -radius; dx <= radius && iterations < MAX_ITERATIONS; dx++) {
        for (let dz = -radius; dz <= radius && iterations < MAX_ITERATIONS; dz++) {
          if (Math.abs(dx) !== radius && Math.abs(dz) !== radius) continue; // Only check perimeter

          iterations++;
          const cx = centerCellX + dx;
          const cz = centerCellZ + dz;

          if (cx < cellMinX || cx > cellMaxX || cz < cellMinZ || cz > cellMaxZ) continue;

          if (grid.isWalkable(cx, cz)) {
            const worldPos = grid.cellToWorld(cx, cz);
            return { x: worldPos.x, z: worldPos.z };
          }
        }
      }

      // Also check center on first iteration
      if (radius === 0 && grid.isWalkable(centerCellX, centerCellZ)) {
        const worldPos = grid.cellToWorld(centerCellX, centerCellZ);
        return { x: worldPos.x, z: worldPos.z };
      }
    }

    // No walkable cell found in region
    return null;
  }

  /**
   * Find a walkable cell near a target point
   * @param {NavNode} node - The node to search in
   * @param {number} targetX - Target world X
   * @param {number} targetZ - Target world Z
   * @param {number} maxRadiusCells - Maximum search radius in cells (default: 10)
   * @private
   */
  _findWalkableNearPoint(node, targetX, targetZ, maxRadiusCells = 10) {
    if (!node.hasGrid()) {
      return { x: targetX, z: targetZ };
    }

    const grid = node.grid;

    // Convert target to cell coordinates
    const targetCellX = Math.floor((targetX - grid.minX) / grid.cellSize);
    const targetCellZ = Math.floor((targetZ - grid.minZ) / grid.cellSize);

    // Spiral search from target with limited radius
    const maxRadius = Math.min(maxRadiusCells, Math.max(grid.width, grid.height));

    for (let radius = 0; radius <= maxRadius; radius++) {
      for (let dx = -radius; dx <= radius; dx++) {
        for (let dz = -radius; dz <= radius; dz++) {
          if (radius > 0 && Math.abs(dx) !== radius && Math.abs(dz) !== radius) continue;

          const cx = targetCellX + dx;
          const cz = targetCellZ + dz;

          if (!grid.inBounds(cx, cz)) continue;

          if (grid.isWalkable(cx, cz)) {
            const worldPos = grid.cellToWorld(cx, cz);
            return { x: worldPos.x, z: worldPos.z };
          }
        }
      }
    }

    return null;
  }

  /**
   * Find a walkable edge point on fromNode toward toNode
   * @private
   */
  _findWalkableEdgePoint(fromNode, toNode) {
    if (!fromNode.hasGrid()) {
      return this._findEdgePointFallback(fromNode, toNode);
    }

    const grid = fromNode.grid;
    const dx = toNode.x - fromNode.x;
    const dz = toNode.z - fromNode.z;

    // Determine which edge to search along
    let edgeCells = [];

    if (Math.abs(dx) > Math.abs(dz)) {
      // Primarily X direction - search along X edge
      const edgeCellX = dx > 0 ? grid.width - 1 : 0;

      // Find Z range that overlaps with toNode
      const overlapMinZ = Math.max(fromNode.minZ, toNode.minZ);
      const overlapMaxZ = Math.min(fromNode.maxZ, toNode.maxZ);
      const cellMinZ = Math.max(0, Math.floor((overlapMinZ - grid.minZ) / grid.cellSize));
      const cellMaxZ = Math.min(grid.height - 1, Math.floor((overlapMaxZ - grid.minZ) / grid.cellSize));

      // Collect edge cells, preferring those closer to toNode's center
      const targetZ = toNode.z;
      for (let cz = cellMinZ; cz <= cellMaxZ; cz++) {
        if (grid.isWalkable(edgeCellX, cz)) {
          const worldPos = grid.cellToWorld(edgeCellX, cz);
          const distToTarget = Math.abs(worldPos.z - targetZ);
          edgeCells.push({ x: edgeCellX, z: cz, dist: distToTarget });
        }
      }
    } else {
      // Primarily Z direction - search along Z edge
      const edgeCellZ = dz > 0 ? grid.height - 1 : 0;

      // Find X range that overlaps with toNode
      const overlapMinX = Math.max(fromNode.minX, toNode.minX);
      const overlapMaxX = Math.min(fromNode.maxX, toNode.maxX);
      const cellMinX = Math.max(0, Math.floor((overlapMinX - grid.minX) / grid.cellSize));
      const cellMaxX = Math.min(grid.width - 1, Math.floor((overlapMaxX - grid.minX) / grid.cellSize));

      // Collect edge cells, preferring those closer to toNode's center
      const targetX = toNode.x;
      for (let cx = cellMinX; cx <= cellMaxX; cx++) {
        if (grid.isWalkable(cx, edgeCellZ)) {
          const worldPos = grid.cellToWorld(cx, edgeCellZ);
          const distToTarget = Math.abs(worldPos.x - targetX);
          edgeCells.push({ x: cx, z: edgeCellZ, dist: distToTarget });
        }
      }
    }

    // Sort by distance to target and pick closest
    if (edgeCells.length > 0) {
      edgeCells.sort((a, b) => a.dist - b.dist);
      const bestCell = edgeCells[0];
      const worldPos = grid.cellToWorld(bestCell.x, bestCell.z);
      return { x: worldPos.x, z: worldPos.z };
    }

    // No walkable edge cell found, expand search inward
    return this._findNearestWalkableToEdge(fromNode, toNode);
  }

  /**
   * Find nearest walkable cell to the edge toward toNode
   * @private
   */
  _findNearestWalkableToEdge(fromNode, toNode) {
    if (!fromNode.hasGrid()) {
      return this._findEdgePointFallback(fromNode, toNode);
    }

    const grid = fromNode.grid;
    const dx = toNode.x - fromNode.x;
    const dz = toNode.z - fromNode.z;

    // Start from edge and search inward
    let startX, startZ, searchDirX, searchDirZ;

    if (Math.abs(dx) > Math.abs(dz)) {
      startX = dx > 0 ? grid.width - 1 : 0;
      startZ = Math.floor(grid.height / 2);
      searchDirX = dx > 0 ? -1 : 1;
      searchDirZ = 0;
    } else {
      startX = Math.floor(grid.width / 2);
      startZ = dz > 0 ? grid.height - 1 : 0;
      searchDirX = 0;
      searchDirZ = dz > 0 ? -1 : 1;
    }

    // Limit search to prevent blocking on large grids
    const maxDepth = Math.min(50, Math.max(grid.width, grid.height));
    const maxOffset = Math.min(50, Math.max(grid.width, grid.height));
    let iterations = 0;
    const MAX_ITERATIONS = 500;

    // Search inward from edge
    for (let depth = 0; depth < maxDepth && iterations < MAX_ITERATIONS; depth++) {
      const cx = startX + searchDirX * depth;
      const cz = startZ + searchDirZ * depth;

      if (!grid.inBounds(cx, cz)) break;

      // Search along the perpendicular axis at this depth
      for (let offset = 0; offset < maxOffset && iterations < MAX_ITERATIONS; offset++) {
        for (const sign of [1, -1]) {
          iterations++;
          let testX = cx;
          let testZ = cz;

          if (searchDirX !== 0) {
            testZ = cz + offset * sign;
          } else {
            testX = cx + offset * sign;
          }

          if (grid.isWalkable(testX, testZ)) {
            const worldPos = grid.cellToWorld(testX, testZ);
            return { x: worldPos.x, z: worldPos.z };
          }
        }

        if (offset === 0) break; // Don't duplicate center
      }
    }

    return null;
  }

  /**
   * Fallback edge point calculation (no grid validation)
   * Uses increased clearance from edges
   * @private
   */
  /**
   * Find transition points for a vertical (fall/jump) transition between edge-adjacent nodes.
   * Returns SAME X,Z for both exit and landing to ensure character lands on destination.
   * @private
   */
  _findVerticalEdgeTransitionPoint(fromNode, toNode) {
    const CLEARANCE = 0.5;

    // Find the XZ overlap between the two nodes
    const overlapMinX = Math.max(fromNode.minX, toNode.minX);
    const overlapMaxX = Math.min(fromNode.maxX, toNode.maxX);
    const overlapMinZ = Math.max(fromNode.minZ, toNode.minZ);
    const overlapMaxZ = Math.min(fromNode.maxZ, toNode.maxZ);

    let x, z;

    // Check if there's actual XZ overlap
    if (overlapMinX < overlapMaxX && overlapMinZ < overlapMaxZ) {
      // Use center of overlap (both surfaces exist at this X,Z)
      x = Math.max(overlapMinX + CLEARANCE, Math.min(overlapMaxX - CLEARANCE, (overlapMinX + overlapMaxX) / 2));
      z = Math.max(overlapMinZ + CLEARANCE, Math.min(overlapMaxZ - CLEARANCE, (overlapMinZ + overlapMaxZ) / 2));
    } else {
      // Edge-adjacent with no XZ overlap - find the shared edge line
      const dx = toNode.x - fromNode.x;
      const dz = toNode.z - fromNode.z;

      if (Math.abs(dx) > Math.abs(dz)) {
        // X-direction: find point on shared X edge within Z overlap
        x = dx > 0 ? (fromNode.maxX + toNode.minX) / 2 : (fromNode.minX + toNode.maxX) / 2;
        const zOverlapMin = Math.max(fromNode.minZ, toNode.minZ);
        const zOverlapMax = Math.min(fromNode.maxZ, toNode.maxZ);
        z = (zOverlapMin + zOverlapMax) / 2;
      } else {
        // Z-direction: find point on shared Z edge within X overlap
        const xOverlapMin = Math.max(fromNode.minX, toNode.minX);
        const xOverlapMax = Math.min(fromNode.maxX, toNode.maxX);
        x = (xOverlapMin + xOverlapMax) / 2;
        z = dz > 0 ? (fromNode.maxZ + toNode.minZ) / 2 : (fromNode.minZ + toNode.maxZ) / 2;
      }
    }

    // Clamp to be valid on BOTH surfaces
    x = Math.max(Math.max(fromNode.minX, toNode.minX) + 0.1,
                 Math.min(Math.min(fromNode.maxX, toNode.maxX) - 0.1, x));
    z = Math.max(Math.max(fromNode.minZ, toNode.minZ) + 0.1,
                 Math.min(Math.min(fromNode.maxZ, toNode.maxZ) - 0.1, z));

    Debug.log('PathFinder', '_findVerticalEdgeTransitionPoint: from', fromNode.cuboidKey,
              'to', toNode.cuboidKey, '-> shared point', x.toFixed(1), z.toFixed(1));

    return { x, z };
  }

  _findEdgePointFallback(fromNode, toNode) {
    const CLEARANCE = 0.5;

    // Find the actual overlap/adjacency region between the two nodes
    const overlapMinX = Math.max(fromNode.minX, toNode.minX);
    const overlapMaxX = Math.min(fromNode.maxX, toNode.maxX);
    const overlapMinZ = Math.max(fromNode.minZ, toNode.minZ);
    const overlapMaxZ = Math.min(fromNode.maxZ, toNode.maxZ);

    const dx = toNode.x - fromNode.x;
    const dz = toNode.z - fromNode.z;

    let x, z;

    if (Math.abs(dx) > Math.abs(dz)) {
      // X-direction transition - exit from the X edge facing toNode
      x = dx > 0 ? fromNode.maxX - CLEARANCE : fromNode.minX + CLEARANCE;
      // Z should be within the SHARED Z overlap region, not toNode's center
      const sharedZCenter = (overlapMinZ + overlapMaxZ) / 2;
      z = Math.max(fromNode.minZ + CLEARANCE, Math.min(fromNode.maxZ - CLEARANCE, sharedZCenter));
    } else {
      // Z-direction transition - exit from the Z edge facing toNode
      // X should be within the SHARED X overlap region, not toNode's center
      const sharedXCenter = (overlapMinX + overlapMaxX) / 2;
      x = Math.max(fromNode.minX + CLEARANCE, Math.min(fromNode.maxX - CLEARANCE, sharedXCenter));
      z = dz > 0 ? fromNode.maxZ - CLEARANCE : fromNode.minZ + CLEARANCE;
    }

    // Ensure point is within node bounds
    x = Math.max(fromNode.minX + 0.1, Math.min(fromNode.maxX - 0.1, x));
    z = Math.max(fromNode.minZ + 0.1, Math.min(fromNode.maxZ - 0.1, z));

    Debug.log('PathFinder', '_findEdgePointFallback: from', fromNode.cuboidKey,
              'to', toNode.cuboidKey, '-> edge point', x.toFixed(1), z.toFixed(1),
              'shared region X=[' + overlapMinX.toFixed(1) + ',' + overlapMaxX.toFixed(1) + ']',
              'Z=[' + overlapMinZ.toFixed(1) + ',' + overlapMaxZ.toFixed(1) + ']');

    return { x, z };
  }

  /**
   * A* search on navigation graph (high-level)
   * @private
   */
  _astar(startNode, endNode, blockedEdges = null) {
    const MAX_ITERATIONS = 500; // Nav graph is small, shouldn't need many iterations
    const openSet = [{ node: startNode, f: 0 }];
    const cameFrom = new Map();
    const gScore = new Map();
    const fScore = new Map();
    const closedSet = new Set();

    gScore.set(startNode, 0);
    fScore.set(startNode, this._heuristic(startNode, endNode));

    let iterations = 0;
    while (openSet.length > 0) {
      // Prevent blocking on pathologically complex graphs
      if (++iterations > MAX_ITERATIONS) {
        Debug.warn('PathFinder', 'A* exceeded max iterations, returning partial path');
        // Return best path found so far
        let bestNode = null;
        let bestDist = Infinity;
        for (const node of closedSet) {
          const dist = this._heuristic(node, endNode);
          if (dist < bestDist) {
            bestDist = dist;
            bestNode = node;
          }
        }
        if (bestNode) {
          return this._reconstructPath(cameFrom, bestNode);
        }
        return null;
      }

      // Get node with lowest f score
      const current = openSet.shift().node;

      if (current === endNode) {
        return this._reconstructPath(cameFrom, current);
      }

      closedSet.add(current);

      for (const { node: neighbor, cost } of current.neighbors) {
        if (closedSet.has(neighbor)) continue;

        // Skip edges that have been blocked (failed during path composition)
        if (blockedEdges && blockedEdges.has(current.cuboidKey + '->' + neighbor.cuboidKey)) continue;

        const tentativeG = gScore.get(current) + cost;

        if (!gScore.has(neighbor) || tentativeG < gScore.get(neighbor)) {
          cameFrom.set(neighbor, current);
          gScore.set(neighbor, tentativeG);
          const f = tentativeG + this._heuristic(neighbor, endNode);
          fScore.set(neighbor, f);

          // Add to open set if not present
          const existingIndex = openSet.findIndex(item => item.node === neighbor);
          if (existingIndex === -1) {
            this._insertSorted(openSet, { node: neighbor, f });
          } else {
            openSet[existingIndex].f = f;
            openSet.sort((a, b) => a.f - b.f);
          }
        }
      }
    }

    return null;
  }

  /**
   * Heuristic function - uses closest point distance for better accuracy
   * Asymmetric height penalty: going up is harder than falling down
   * @private
   */
  _heuristic(from, to) {
    // For overlapping surfaces, use the closest point distance
    // This gives a more accurate estimate than center-to-center
    const closestX = Math.max(to.minX, Math.min(to.maxX, from.x));
    const closestZ = Math.max(to.minZ, Math.min(to.maxZ, from.z));

    const dx = closestX - from.x;
    const dy = to.y - from.y;
    const dz = closestZ - from.z;

    // Asymmetric height penalty: climbing up is harder, falling down is easy
    let heightPenalty;
    if (dy > 0) {
      // Going UP - penalize more
      heightPenalty = dy * 0.5;
    } else {
      // Going DOWN - minimal penalty (gravity is free!)
      heightPenalty = Math.abs(dy) * 0.05;
    }

    return Math.sqrt(dx * dx + dy * dy + dz * dz) + heightPenalty;
  }

  /**
   * Reconstruct path from A* result
   * @private
   */
  _reconstructPath(cameFrom, current) {
    const path = [current];
    while (cameFrom.has(current)) {
      current = cameFrom.get(current);
      path.unshift(current);
    }
    return path;
  }

  /**
   * Insert item into sorted array
   * @private
   */
  _insertSorted(arr, item) {
    let i = 0;
    while (i < arr.length && arr[i].f < item.f) {
      i++;
    }
    arr.splice(i, 0, item);
  }

  /**
   * Smooth path using line-of-sight checks (single surface)
   * @private
   */
  _smoothPath(waypoints, surfaceY) {
    if (waypoints.length <= 2) return waypoints;

    Debug.log('PathFinder', '_smoothPath input:', waypoints.length, 'waypoints at Y=', surfaceY);

    const smoothed = [waypoints[0]];
    let current = 0;

    while (current < waypoints.length - 1) {
      // Try to skip to furthest visible waypoint
      let furthest = current + 1;

      for (let i = waypoints.length - 1; i > current + 1; i--) {
        if (this._hasLineOfSight(waypoints[current], waypoints[i], surfaceY)) {
          furthest = i;
          if (i > current + 1) {
            Debug.log('PathFinder', '_smoothPath: Skipping from', current, 'to', i,
                      '(', waypoints[current].x.toFixed(1), ',', waypoints[current].z.toFixed(1),
                      '->', waypoints[i].x.toFixed(1), ',', waypoints[i].z.toFixed(1), ')');
          }
          break;
        }
      }

      smoothed.push(waypoints[furthest]);
      current = furthest;
    }

    Debug.log('PathFinder', '_smoothPath output:', smoothed.length, 'waypoints');
    return smoothed;
  }

  /**
   * Smooth path that may have height changes
   * Intelligently removes unnecessary waypoints while preserving critical transitions
   * @private
   */
  _smoothPathWithHeightChanges(waypoints) {
    if (waypoints.length <= 2) return waypoints;

    Debug.log('PathFinder', '_smoothPathWithHeightChanges INPUT:', waypoints.length, 'waypoints');
    for (let i = 0; i < Math.min(waypoints.length, 10); i++) {
      const wp = waypoints[i];
      Debug.log('PathFinder', '  [' + i + '] x=' + wp.x.toFixed(2) + ' y=' + wp.y.toFixed(2) + ' z=' + wp.z.toFixed(2) + ' type=' + (wp.type || 'walk'));
    }
    if (waypoints.length > 10) {
      Debug.log('PathFinder', '  ... and', waypoints.length - 10, 'more');
    }

    // First pass: merge consecutive waypoints at same height that have line of sight
    let merged = this._mergeConsecutiveSameHeight(waypoints);

    Debug.log('PathFinder', '_mergeConsecutiveSameHeight OUTPUT:', merged.length, 'waypoints');
    for (let i = 0; i < Math.min(merged.length, 10); i++) {
      const wp = merged[i];
      Debug.log('PathFinder', '  [' + i + '] x=' + wp.x.toFixed(2) + ' y=' + wp.y.toFixed(2) + ' z=' + wp.z.toFixed(2) + ' type=' + (wp.type || 'walk'));
    }

    // Second pass: optimize transition sequences
    merged = this._optimizeTransitionSequences(merged);
    Debug.log('PathFinder', '_optimizeTransitionSequences OUTPUT:', merged.length, 'waypoints');

    // Third pass: remove redundant waypoints
    merged = this._removeRedundantWaypoints(merged);
    Debug.log('PathFinder', '_removeRedundantWaypoints OUTPUT:', merged.length, 'waypoints');

    // Fourth pass: add corner padding where path turns sharply near obstacles
    merged = this._addCornerPadding(merged);
    Debug.log('PathFinder', '_addCornerPadding FINAL OUTPUT:', merged.length, 'waypoints');
    for (let i = 0; i < Math.min(merged.length, 15); i++) {
      const wp = merged[i];
      Debug.log('PathFinder', '  FINAL [' + i + '] x=' + wp.x.toFixed(2) + ' y=' + wp.y.toFixed(2) + ' z=' + wp.z.toFixed(2) + ' type=' + (wp.type || 'walk'));
    }

    return merged;
  }

  /**
   * Add waypoints at sharp corners to keep character away from obstacle edges
   * @private
   */
  _addCornerPadding(waypoints) {
    if (waypoints.length <= 2 || !this.collisionSystem) return waypoints;

    const result = [waypoints[0]];
    const CORNER_PADDING = 0.6; // Distance to keep from corners

    for (let i = 1; i < waypoints.length; i++) {
      const prev = result[result.length - 1];
      const current = waypoints[i];

      // Check if we need corner padding between prev and current
      if (Math.abs(prev.y - current.y) < 0.1) {
        const cornerPoints = this._findAllCornerPadPoints(prev, current, CORNER_PADDING);
        for (const cp of cornerPoints) {
          // CRITICAL: Validate that we can actually reach the pad point from the previous waypoint
          // This prevents adding pad points that would require walking through walls
          const lastResultPoint = result[result.length - 1];
          if (this._hasLineOfSight(lastResultPoint, cp, cp.y)) {
            result.push(cp);
          } else {
            Debug.log('PathFinder', '_addCornerPadding: Skipping unreachable pad point at',
                      cp.x.toFixed(1), cp.z.toFixed(1), 'from', lastResultPoint.x.toFixed(1), lastResultPoint.z.toFixed(1));
          }
        }
      }

      result.push(current);
    }

    return result;
  }

  /**
   * Find all corner padding points needed along a path segment
   * @private
   */
  _findAllCornerPadPoints(from, to, padding) {
    if (!this.collisionSystem) return [];

    const dx = to.x - from.x;
    const dz = to.z - from.z;
    const distance = Math.sqrt(dx * dx + dz * dz);

    if (distance < 0.5) return [];

    const CHARACTER_WIDTH = 0.9;
    const CHARACTER_HEIGHT = 2.0;
    const CHARACTER_DEPTH = 0.9;
    const surfaceY = from.y;

    // Find all obstacles near the path
    const pathMinX = Math.min(from.x, to.x) - CHARACTER_WIDTH - padding;
    const pathMaxX = Math.max(from.x, to.x) + CHARACTER_WIDTH + padding;
    const pathMinZ = Math.min(from.z, to.z) - CHARACTER_DEPTH - padding;
    const pathMaxZ = Math.max(from.z, to.z) + CHARACTER_DEPTH + padding;

    const queryBox = {
      minX: pathMinX,
      maxX: pathMaxX,
      minY: surfaceY + 0.01,
      maxY: surfaceY + CHARACTER_HEIGHT,
      minZ: pathMinZ,
      maxZ: pathMaxZ
    };

    const obstacles = this.collisionSystem.queryCuboids(queryBox);
    const padPoints = [];
    const requiredClearance = Math.max(CHARACTER_WIDTH, CHARACTER_DEPTH) / 2 + padding;

    for (const obstacle of obstacles) {
      const obsTop = obstacle.y + obstacle.height;
      if (Math.abs(obsTop - surfaceY) < 0.1) continue;
      if (obsTop <= surfaceY + 0.01) continue;
      if (obstacle.y >= surfaceY + CHARACTER_HEIGHT) continue;

      const obsMinX = obstacle.x;
      const obsMaxX = obstacle.x + obstacle.width;
      const obsMinZ = obstacle.z;
      const obsMaxZ = obstacle.z + obstacle.depth;

      // Check each corner of the obstacle
      const corners = [
        { x: obsMinX, z: obsMinZ },
        { x: obsMinX, z: obsMaxZ },
        { x: obsMaxX, z: obsMinZ },
        { x: obsMaxX, z: obsMaxZ }
      ];

      for (const corner of corners) {
        // Find closest point on path segment to this corner
        const closestT = this._closestPointOnSegment(corner.x, corner.z, from.x, from.z, to.x, to.z);
        const closestX = from.x + dx * closestT;
        const closestZ = from.z + dz * closestT;

        const distToPath = Math.sqrt(
          Math.pow(corner.x - closestX, 2) + Math.pow(corner.z - closestZ, 2)
        );

        // If path passes too close to corner
        if (distToPath < requiredClearance) {
          // Calculate direction away from corner
          const awayX = closestX - corner.x;
          const awayZ = closestZ - corner.z;
          const awayLen = Math.sqrt(awayX * awayX + awayZ * awayZ);

          if (awayLen > 0.01) {
            // Create waypoint pushed away from corner
            const padX = corner.x + (awayX / awayLen) * (requiredClearance + 0.2);
            const padZ = corner.z + (awayZ / awayLen) * (requiredClearance + 0.2);

            // Check if this point is valid (not inside another obstacle)
            if (!this._isPointBlocked(padX, padZ, surfaceY)) {
              Debug.log('PathFinder', '_findAllCornerPadPoints: Adding pad point at',
                        padX.toFixed(1), padZ.toFixed(1),
                        'for corner', corner.x.toFixed(1), corner.z.toFixed(1),
                        'of obstacle X=[' + obsMinX.toFixed(1) + ',' + obsMaxX.toFixed(1) + ']',
                        'Z=[' + obsMinZ.toFixed(1) + ',' + obsMaxZ.toFixed(1) + ']');
              padPoints.push({
                x: padX,
                y: surfaceY,
                z: padZ,
                type: 'walk',
                t: closestT // For sorting
              });
            }
          }
        }
      }
    }

    // Sort pad points by position along the path segment
    padPoints.sort((a, b) => a.t - b.t);

    // Remove duplicate/close points
    const filtered = [];
    for (const pt of padPoints) {
      const isDuplicate = filtered.some(existing =>
        Math.sqrt(Math.pow(existing.x - pt.x, 2) + Math.pow(existing.z - pt.z, 2)) < 0.3
      );
      if (!isDuplicate) {
        filtered.push({ x: pt.x, y: pt.y, z: pt.z, type: pt.type });
      }
    }

    return filtered;
  }

  /**
   * Find closest point on a line segment (returns t parameter 0-1)
   * @private
   */
  _closestPointOnSegment(px, pz, x1, z1, x2, z2) {
    const dx = x2 - x1;
    const dz = z2 - z1;
    const lenSq = dx * dx + dz * dz;

    if (lenSq < 0.0001) return 0;

    let t = ((px - x1) * dx + (pz - z1) * dz) / lenSq;
    return Math.max(0, Math.min(1, t));
  }

  /**
   * Check if a point is blocked by an obstacle
   * @private
   */
  _isPointBlocked(x, z, surfaceY) {
    if (!this.collisionSystem) return false;

    const CHARACTER_WIDTH = 0.9;
    const CHARACTER_HEIGHT = 2.0;
    const CHARACTER_DEPTH = 0.9;

    const queryBox = {
      minX: x - CHARACTER_WIDTH / 2,
      maxX: x + CHARACTER_WIDTH / 2,
      minY: surfaceY + 0.01,
      maxY: surfaceY + CHARACTER_HEIGHT,
      minZ: z - CHARACTER_DEPTH / 2,
      maxZ: z + CHARACTER_DEPTH / 2
    };

    const obstacles = this.collisionSystem.queryCuboids(queryBox);

    for (const obstacle of obstacles) {
      const obsTop = obstacle.y + obstacle.height;
      if (Math.abs(obsTop - surfaceY) < 0.1) continue;
      if (obsTop <= surfaceY + 0.01) continue;

      if (obstacle.y < surfaceY + CHARACTER_HEIGHT) {
        const obsMinX = obstacle.x;
        const obsMaxX = obstacle.x + obstacle.width;
        const obsMinZ = obstacle.z;
        const obsMaxZ = obstacle.z + obstacle.depth;

        if (queryBox.minX < obsMaxX && queryBox.maxX > obsMinX &&
            queryBox.minZ < obsMaxZ && queryBox.maxZ > obsMinZ) {
          return true;
        }
      }
    }

    return false;
  }


  /**
   * Merge consecutive waypoints at the same height level
   * @private
   */
  _mergeConsecutiveSameHeight(waypoints) {
    if (waypoints.length <= 2) return waypoints;

    const result = [waypoints[0]];
    let i = 0;

    while (i < waypoints.length - 1) {
      const current = waypoints[i];
      let furthest = i + 1;

      // Look for furthest waypoint at same height with line of sight
      for (let j = waypoints.length - 1; j > i; j--) {
        const target = waypoints[j];

        // Check if all intermediate waypoints are at same height and walkable
        let canMerge = Math.abs(target.y - current.y) < 0.1;

        if (canMerge) {
          for (let k = i + 1; k < j; k++) {
            const wp = waypoints[k];
            // Can't skip across height changes or non-walk transitions
            if (Math.abs(wp.y - current.y) > 0.1 || (wp.type && wp.type !== 'walk')) {
              canMerge = false;
              break;
            }
          }
        }

        if (canMerge) {
          const hasLOS = this._hasLineOfSight(current, target, current.y);
          Debug.log('PathFinder', '_mergeConsecutiveSameHeight LOS check:',
                    'from [' + i + '] (' + current.x.toFixed(1) + ',' + current.z.toFixed(1) + ')',
                    'to [' + j + '] (' + target.x.toFixed(1) + ',' + target.z.toFixed(1) + ')',
                    'at Y=' + current.y.toFixed(1),
                    'skipping', (j - i - 1), 'waypoints',
                    '=> LOS=' + hasLOS);
          if (hasLOS) {
            // Log what we're skipping
            for (let skip = i + 1; skip < j; skip++) {
              Debug.warn('PathFinder', '  SKIPPING waypoint [' + skip + '] at',
                        waypoints[skip].x.toFixed(1) + ',' + waypoints[skip].y.toFixed(1) + ',' + waypoints[skip].z.toFixed(1));
            }
            furthest = j;
            break;
          }
        }
      }

      Debug.log('PathFinder', '_mergeConsecutiveSameHeight: from [' + i + '] jump to [' + furthest + ']');
      result.push(waypoints[furthest]);
      i = furthest;
    }

    return result;
  }

  /**
   * Optimize sequences of vertical transitions (staircases)
   * @private
   */
  _optimizeTransitionSequences(waypoints) {
    if (waypoints.length <= 3) return waypoints;

    const result = [waypoints[0]];

    for (let i = 1; i < waypoints.length; i++) {
      const prev = result[result.length - 1];
      const current = waypoints[i];

      // Check if this is a transition waypoint
      const isTransition = current.type === 'jump' || current.type === 'fall';

      if (isTransition && i < waypoints.length - 1) {
        const next = waypoints[i + 1];

        // If next waypoint is very close in XZ (less than 1 unit), merge them
        const distXZ = Math.sqrt(
          Math.pow(next.x - current.x, 2) + Math.pow(next.z - current.z, 2)
        );

        if (distXZ < 1.0 && next.type === 'walk') {
          // Skip the current transition and use the next point with the transition type
          result.push({
            x: next.x,
            y: next.y,
            z: next.z,
            type: current.type
          });
          i++; // Skip the next waypoint since we merged it
          continue;
        }
      }

      // Check if current is redundant (very close to previous)
      const distToPrev = Math.sqrt(
        Math.pow(current.x - prev.x, 2) +
        Math.pow(current.z - prev.z, 2)
      );

      if (distToPrev < 0.3 && Math.abs(current.y - prev.y) < 0.1) {
        // Skip this waypoint, it's too close to previous
        continue;
      }

      result.push(current);
    }

    return result;
  }

  /**
   * Remove waypoints that are redundant (collinear or very close)
   * @private
   */
  _removeRedundantWaypoints(waypoints) {
    if (waypoints.length <= 2) return waypoints;

    const result = [waypoints[0]];

    for (let i = 1; i < waypoints.length - 1; i++) {
      const prev = result[result.length - 1];
      const current = waypoints[i];
      const next = waypoints[i + 1];

      // Always keep transition waypoints
      if (current.type === 'jump' || current.type === 'fall') {
        result.push(current);
        continue;
      }

      // Check if current is collinear with prev and next
      if (Math.abs(prev.y - current.y) < 0.1 && Math.abs(current.y - next.y) < 0.1) {
        const dx1 = current.x - prev.x;
        const dz1 = current.z - prev.z;
        const dx2 = next.x - current.x;
        const dz2 = next.z - current.z;

        // Cross product to check collinearity
        const cross = dx1 * dz2 - dz1 * dx2;

        if (Math.abs(cross) < 0.1) {
          // Collinear - skip this waypoint
          continue;
        }
      }

      result.push(current);
    }

    // Always keep last waypoint
    result.push(waypoints[waypoints.length - 1]);

    return result;
  }

  /**
   * Check if there's a clear line of sight between two points
   * Uses full character width plus generous corner margin for safety
   * @private
   */
  _hasLineOfSight(from, to, surfaceY) {
    if (!this.collisionSystem) {
      // No collision system - assume line of sight
      Debug.log('PathFinder', '_hasLineOfSight: No collision system, returning true');
      return true;
    }

    // Sample points along the line
    const dx = to.x - from.x;
    const dz = to.z - from.z;
    const distance = Math.sqrt(dx * dx + dz * dz);

    if (distance < 0.3) return true;

    // FIRST: Check for continuous surface coverage along the entire line
    // This catches holes that sampling might miss due to aliasing
    if (!this._hasContiguousSurfaceCoverage(from, to, surfaceY)) {
      Debug.log('PathFinder', '_hasLineOfSight: No contiguous surface coverage from',
                from.x.toFixed(1) + ',' + from.z.toFixed(1), 'to', to.x.toFixed(1) + ',' + to.z.toFixed(1));
      return false;
    }

    // Adaptive step size: finer for short distances, coarser for long distances
    // Cap at MAX_SAMPLES to prevent blocking on very long line-of-sight checks
    const MAX_SAMPLES = 100;
    const idealStepSize = 0.2;
    const minSteps = Math.ceil(distance / idealStepSize);
    const steps = Math.min(minSteps, MAX_SAMPLES);
    const stepX = dx / steps;
    const stepZ = dz / steps;

    const CHARACTER_WIDTH = 0.9;
    const CHARACTER_HEIGHT = 2.0;
    const CHARACTER_DEPTH = 0.9;
    // Extra margin to account for turning and prevent corner clipping
    const CORNER_MARGIN = 0.4;

    const halfWidth = CHARACTER_WIDTH / 2 + CORNER_MARGIN;
    const halfDepth = CHARACTER_DEPTH / 2 + CORNER_MARGIN;

    let totalObstaclesFound = 0;

    // Track surface continuity to prevent corner cutting across holes
    const startSurface = this._hasSurfaceAt(from.x, from.z, surfaceY, true);
    let prevCuboid = startSurface.found ? startSurface.cuboid : null;
    let prevPoint = { x: from.x, z: from.z };

    for (let i = 0; i <= steps; i++) {
      const x = from.x + stepX * i;
      const z = from.z + stepZ * i;
      const currPoint = { x, z };

      // Check 1: Verify there's a surface underneath this point (prevents walking over gaps)
      const surfaceCheck = this._hasSurfaceAt(x, z, surfaceY, true);
      if (!surfaceCheck.found) {
        Debug.log('PathFinder', '_hasLineOfSight: No surface at', x.toFixed(1), z.toFixed(1), 'Y=' + surfaceY.toFixed(1));
        return false; // No ground here - can't walk over gap
      }

      // Check 2: Surface continuity - when cuboid changes, validate the transition
      if (prevCuboid && surfaceCheck.cuboid && surfaceCheck.cuboid !== prevCuboid) {
        // Verify heights match (coplanar check)
        const prevTop = prevCuboid.y + prevCuboid.height;
        const currTop = surfaceCheck.cuboid.y + surfaceCheck.cuboid.height;
        if (Math.abs(prevTop - currTop) > 0.2) {
          Debug.log('PathFinder', '_hasLineOfSight: Height mismatch at', x.toFixed(1), z.toFixed(1));
          return false;
        }

        // CRITICAL: Check if we skipped over any surfaces between samples
        // Query all surfaces along the line segment between prev and current sample
        const skippedSurfaces = this._findSurfacesBetweenPoints(prevPoint, currPoint, surfaceY, prevCuboid, surfaceCheck.cuboid);
        if (skippedSurfaces.length > 0) {
          Debug.log('PathFinder', '_hasLineOfSight: Skipped over', skippedSurfaces.length, 'surfaces between samples');
          return false;
        }

        // Verify cuboids share an edge (are truly adjacent)
        if (!this._cuboidsShareEdge(prevCuboid, surfaceCheck.cuboid)) {
          Debug.log('PathFinder', '_hasLineOfSight: Cuboids do not share edge at', x.toFixed(1), z.toFixed(1));
          return false;
        }

        // Verify the line actually crosses through the shared edge (prevents corner cutting)
        if (!this._transitionCrossesSharedEdge(prevPoint, currPoint, prevCuboid, surfaceCheck.cuboid)) {
          Debug.log('PathFinder', '_hasLineOfSight: Line does not cross shared edge at', x.toFixed(1), z.toFixed(1));
          return false;
        }

        prevCuboid = surfaceCheck.cuboid;
      }
      prevPoint = currPoint;

      // Check 3: Query for obstacles at this point with corner margin
      const queryBox = {
        minX: x - halfWidth,
        maxX: x + halfWidth,
        minY: surfaceY + 0.01,
        maxY: surfaceY + CHARACTER_HEIGHT,
        minZ: z - halfDepth,
        maxZ: z + halfDepth
      };

      const obstacles = this.collisionSystem.queryCuboids(queryBox);
      totalObstaclesFound += obstacles.size;

      for (const obstacle of obstacles) {
        const obsTop = obstacle.y + obstacle.height;
        const obsBottom = obstacle.y;

        // Skip if this is our floor
        if (Math.abs(obsTop - surfaceY) < 0.1) continue;
        if (obsTop <= surfaceY + 0.01) continue;

        // NOTE: We do NOT skip stacked surfaces here (unlike in _isPointPhysicallyWalkableOnNode).
        // Stacked surfaces (like steps) DO block horizontal movement on the current surface.
        // You can STAND next to them, but you can't WALK through them.

        // Check if obstacle blocks the path
        if (obsBottom < surfaceY + CHARACTER_HEIGHT) {
          const obsMinX = obstacle.x;
          const obsMaxX = obstacle.x + obstacle.width;
          const obsMinZ = obstacle.z;
          const obsMaxZ = obstacle.z + obstacle.depth;

          if (queryBox.minX < obsMaxX && queryBox.maxX > obsMinX &&
              queryBox.minZ < obsMaxZ && queryBox.maxZ > obsMinZ) {
            Debug.log('PathFinder', '_hasLineOfSight BLOCKED at step', i, '/',  steps,
                      'pos=(' + x.toFixed(1) + ',' + z.toFixed(1) + ')',
                      'by obstacle X=[' + obsMinX.toFixed(1) + ',' + obsMaxX.toFixed(1) + ']',
                      'Z=[' + obsMinZ.toFixed(1) + ',' + obsMaxZ.toFixed(1) + ']',
                      'Y=[' + obsBottom.toFixed(1) + ',' + obsTop.toFixed(1) + ']');
            return false;
          }
        }
      }
    }

    Debug.log('PathFinder', '_hasLineOfSight: clear path, checked', steps, 'steps, found', totalObstaclesFound, 'total obstacles (all skipped)');
    return true;
  }

  /**
   * Check if there's a walkable surface at the given position
   * @param {number} x - X coordinate
   * @param {number} z - Z coordinate
   * @param {number} surfaceY - Expected surface Y height
   * @param {boolean} returnCuboid - If true, returns {found, cuboid} instead of boolean
   * @returns {boolean|{found: boolean, cuboid: Object|null}} True if surface exists
   * @private
   */
  _hasSurfaceAt(x, z, surfaceY, returnCuboid = false) {
    if (!this.collisionSystem) {
      return returnCuboid ? { found: true, cuboid: null } : true;
    }

    // Query for surfaces that could be the floor at this point
    const queryBox = {
      minX: x - 0.1,
      maxX: x + 0.1,
      minY: surfaceY - 0.5, // Allow some tolerance below
      maxY: surfaceY + 0.5, // And above
      minZ: z - 0.1,
      maxZ: z + 0.1
    };

    const cuboids = this.collisionSystem.queryCuboids(queryBox);

    for (const cuboid of cuboids) {
      const cuboidTop = cuboid.y + cuboid.height;
      // Check if this cuboid's top surface is at our expected height
      if (Math.abs(cuboidTop - surfaceY) < 0.3) {
        // And the point is within the cuboid's XZ bounds
        if (x >= cuboid.x && x <= cuboid.x + cuboid.width &&
            z >= cuboid.z && z <= cuboid.z + cuboid.depth) {
          return returnCuboid ? { found: true, cuboid } : true;
        }
      }
    }

    return returnCuboid ? { found: false, cuboid: null } : false;
  }

  /**
   * Check if two cuboids share an edge (are truly adjacent, not just overlapping bounding boxes).
   * For walking between surfaces, they must share an edge along one axis with overlap on the other.
   * @param {Object} a - First cuboid
   * @param {Object} b - Second cuboid
   * @returns {boolean} True if cuboids share a walkable edge
   * @private
   */
  _cuboidsShareEdge(a, b) {
    if (!a || !b) return true; // If either is null, assume valid

    const aMinX = a.x, aMaxX = a.x + a.width;
    const aMinZ = a.z, aMaxZ = a.z + a.depth;
    const bMinX = b.x, bMaxX = b.x + b.width;
    const bMinZ = b.z, bMaxZ = b.z + b.depth;

    // Tolerance for edge adjacency
    const edgeTol = 0.5;
    const minOverlap = 0.1;

    // Calculate overlaps
    const overlapX = Math.min(aMaxX, bMaxX) - Math.max(aMinX, bMinX);
    const overlapZ = Math.min(aMaxZ, bMaxZ) - Math.max(aMinZ, bMinZ);

    // Case 1: Cuboids overlap in XZ (stacked vertically) - always valid
    if (overlapX > minOverlap && overlapZ > minOverlap) {
      return true;
    }

    // Case 2: Share X-edge (one's maxX meets other's minX) with Z overlap
    const shareXEdge = Math.abs(aMaxX - bMinX) < edgeTol || Math.abs(bMaxX - aMinX) < edgeTol;
    if (shareXEdge && overlapZ > minOverlap) {
      return true;
    }

    // Case 3: Share Z-edge (one's maxZ meets other's minZ) with X overlap
    const shareZEdge = Math.abs(aMaxZ - bMinZ) < edgeTol || Math.abs(bMaxZ - aMinZ) < edgeTol;
    if (shareZEdge && overlapX > minOverlap) {
      return true;
    }

    return false;
  }

  /**
   * Verify that a line segment crosses through the shared edge region of two adjacent cuboids.
   * This prevents corner cutting where the path would go through empty space.
   * @param {Object} prevPt - Previous point {x, z}
   * @param {Object} currPt - Current point {x, z}
   * @param {Object} prevCuboid - Cuboid at previous point
   * @param {Object} currCuboid - Cuboid at current point
   * @returns {boolean} True if transition is valid
   * @private
   */
  _transitionCrossesSharedEdge(prevPt, currPt, prevCuboid, currCuboid) {
    if (!prevCuboid || !currCuboid) return true;

    const aMinX = prevCuboid.x, aMaxX = prevCuboid.x + prevCuboid.width;
    const aMinZ = prevCuboid.z, aMaxZ = prevCuboid.z + prevCuboid.depth;
    const bMinX = currCuboid.x, bMaxX = currCuboid.x + currCuboid.width;
    const bMinZ = currCuboid.z, bMaxZ = currCuboid.z + currCuboid.depth;

    const edgeTol = 0.5;
    const minOverlap = 0.1;

    // Calculate overlap region
    const overlapMinX = Math.max(aMinX, bMinX), overlapMaxX = Math.min(aMaxX, bMaxX);
    const overlapMinZ = Math.max(aMinZ, bMinZ), overlapMaxZ = Math.min(aMaxZ, bMaxZ);
    const overlapX = overlapMaxX - overlapMinX;
    const overlapZ = overlapMaxZ - overlapMinZ;

    // If cuboids fully overlap in XZ, just verify point is in overlap region
    if (overlapX > minOverlap && overlapZ > minOverlap) {
      const margin = 0.5;
      return currPt.x >= overlapMinX - margin && currPt.x <= overlapMaxX + margin &&
             currPt.z >= overlapMinZ - margin && currPt.z <= overlapMaxZ + margin;
    }

    const dx = currPt.x - prevPt.x;
    const dz = currPt.z - prevPt.z;

    // X-edge: check if line crosses at valid Z
    const xEdgeAtAMax = Math.abs(aMaxX - bMinX) < edgeTol;
    const xEdgeAtAMin = Math.abs(bMaxX - aMinX) < edgeTol;
    if ((xEdgeAtAMax || xEdgeAtAMin) && overlapZ > minOverlap) {
      const edgeX = xEdgeAtAMax ? aMaxX : aMinX;
      if (Math.abs(dx) > 0.001) {
        const t = (edgeX - prevPt.x) / dx;
        if (t >= -0.1 && t <= 1.1) {
          const crossZ = prevPt.z + t * dz;
          if (crossZ >= overlapMinZ - 0.3 && crossZ <= overlapMaxZ + 0.3) {
            return true;
          }
        }
      }
      return false;
    }

    // Z-edge: check if line crosses at valid X
    const zEdgeAtAMax = Math.abs(aMaxZ - bMinZ) < edgeTol;
    const zEdgeAtAMin = Math.abs(bMaxZ - aMinZ) < edgeTol;
    if ((zEdgeAtAMax || zEdgeAtAMin) && overlapX > minOverlap) {
      const edgeZ = zEdgeAtAMax ? aMaxZ : aMinZ;
      if (Math.abs(dz) > 0.001) {
        const t = (edgeZ - prevPt.z) / dz;
        if (t >= -0.1 && t <= 1.1) {
          const crossX = prevPt.x + t * dx;
          if (crossX >= overlapMinX - 0.3 && crossX <= overlapMaxX + 0.3) {
            return true;
          }
        }
      }
      return false;
    }

    return false;
  }

  /**
   * Find any surfaces that the line segment crosses between two sample points.
   * This detects when samples skip over narrow surfaces entirely.
   * @param {Object} prevPt - Previous sample point {x, z}
   * @param {Object} currPt - Current sample point {x, z}
   * @param {number} surfaceY - The Y height of the surface
   * @param {Object} excludeA - Cuboid to exclude (start)
   * @param {Object} excludeB - Cuboid to exclude (end)
   * @returns {Array} Array of cuboids that were skipped
   * @private
   */
  _findSurfacesBetweenPoints(prevPt, currPt, surfaceY, excludeA, excludeB) {
    if (!this.collisionSystem) return [];

    // Query a box that encompasses the line segment
    const minX = Math.min(prevPt.x, currPt.x) - 0.1;
    const maxX = Math.max(prevPt.x, currPt.x) + 0.1;
    const minZ = Math.min(prevPt.z, currPt.z) - 0.1;
    const maxZ = Math.max(prevPt.z, currPt.z) + 0.1;

    const queryBox = {
      minX, maxX,
      minY: surfaceY - 0.5,
      maxY: surfaceY + 0.5,
      minZ, maxZ
    };

    const cuboids = this.collisionSystem.queryCuboids(queryBox);
    const skipped = [];

    for (const cuboid of cuboids) {
      // Skip the start and end cuboids
      if (cuboid === excludeA || cuboid === excludeB) continue;

      // Check if this cuboid's surface is at the right height
      const cuboidTop = cuboid.y + cuboid.height;
      if (Math.abs(cuboidTop - surfaceY) > 0.3) continue;

      // Check if the line segment actually crosses through this cuboid
      const cMinX = cuboid.x, cMaxX = cuboid.x + cuboid.width;
      const cMinZ = cuboid.z, cMaxZ = cuboid.z + cuboid.depth;

      // Use line-box intersection test
      if (this._lineIntersectsBox(prevPt.x, prevPt.z, currPt.x, currPt.z, cMinX, cMaxX, cMinZ, cMaxZ)) {
        skipped.push(cuboid);
      }
    }

    return skipped;
  }

  /**
   * Check if there's contiguous surface coverage along a line segment.
   * This method queries ALL surfaces that the line passes through and verifies
   * there are no gaps. Unlike point sampling, this catches holes regardless of position.
   * @param {Object} from - Start point {x, z}
   * @param {Object} to - End point {x, z}
   * @param {number} surfaceY - Surface height
   * @returns {boolean} True if there's continuous coverage
   * @private
   */
  _hasContiguousSurfaceCoverage(from, to, surfaceY) {
    if (!this.collisionSystem) return true;

    const dx = to.x - from.x;
    const dz = to.z - from.z;
    const length = Math.sqrt(dx * dx + dz * dz);
    if (length < 0.1) return true;

    // Normalize direction
    const dirX = dx / length;
    const dirZ = dz / length;

    // Query all surfaces in the bounding box of the line
    const minX = Math.min(from.x, to.x) - 0.5;
    const maxX = Math.max(from.x, to.x) + 0.5;
    const minZ = Math.min(from.z, to.z) - 0.5;
    const maxZ = Math.max(from.z, to.z) + 0.5;

    const queryBox = {
      minX, maxX,
      minY: surfaceY - 0.5,
      maxY: surfaceY + 0.5,
      minZ, maxZ
    };

    const cuboids = this.collisionSystem.queryCuboids(queryBox);

    // For each surface, calculate the range of 't' values (along the line) it covers
    // t=0 at 'from', t=1 at 'to'
    const coverageRanges = [];

    for (const cuboid of cuboids) {
      const cuboidTop = cuboid.y + cuboid.height;
      if (Math.abs(cuboidTop - surfaceY) > 0.3) continue;

      const cMinX = cuboid.x, cMaxX = cuboid.x + cuboid.width;
      const cMinZ = cuboid.z, cMaxZ = cuboid.z + cuboid.depth;

      // Find where the line enters and exits this cuboid's XZ bounds
      let tMin = 0, tMax = 1;

      if (Math.abs(dirX) > 0.001) {
        const t1 = (cMinX - from.x) / dx;
        const t2 = (cMaxX - from.x) / dx;
        tMin = Math.max(tMin, Math.min(t1, t2));
        tMax = Math.min(tMax, Math.max(t1, t2));
      } else if (from.x < cMinX || from.x > cMaxX) {
        continue; // Line doesn't intersect in X
      }

      if (Math.abs(dirZ) > 0.001) {
        const t1 = (cMinZ - from.z) / dz;
        const t2 = (cMaxZ - from.z) / dz;
        tMin = Math.max(tMin, Math.min(t1, t2));
        tMax = Math.min(tMax, Math.max(t1, t2));
      } else if (from.z < cMinZ || from.z > cMaxZ) {
        continue; // Line doesn't intersect in Z
      }

      if (tMin < tMax) {
        coverageRanges.push({ start: tMin, end: tMax });
      }
    }

    if (coverageRanges.length === 0) {
      Debug.log('PathFinder', '_hasContiguousSurfaceCoverage: No surfaces found along line');
      return false;
    }

    // Sort ranges by start position
    coverageRanges.sort((a, b) => a.start - b.start);

    // Check for gaps - merge overlapping ranges and verify coverage from 0 to 1
    let currentEnd = coverageRanges[0].start;

    // Check if we start with coverage (first range should start at or before 0)
    if (currentEnd > 0.05) { // Small tolerance for edge cases
      Debug.log('PathFinder', '_hasContiguousSurfaceCoverage: Gap at start, first coverage starts at', currentEnd.toFixed(2));
      return false;
    }

    currentEnd = coverageRanges[0].end;

    for (let i = 1; i < coverageRanges.length; i++) {
      const range = coverageRanges[i];

      // Check for gap (allow small tolerance for touching surfaces)
      if (range.start > currentEnd + 0.05) {
        Debug.log('PathFinder', '_hasContiguousSurfaceCoverage: Gap found from', currentEnd.toFixed(2), 'to', range.start.toFixed(2));
        return false;
      }

      currentEnd = Math.max(currentEnd, range.end);
    }

    // Check if we end with coverage (should extend to at least 0.95)
    if (currentEnd < 0.95) {
      Debug.log('PathFinder', '_hasContiguousSurfaceCoverage: Gap at end, coverage ends at', currentEnd.toFixed(2));
      return false;
    }

    return true;
  }

  /**
   * Check if a direct path exists between two positions
   * @param {Object} from - Start position {x, y, z}
   * @param {Object} to - End position {x, y, z}
   * @returns {boolean}
   */
  hasDirectPath(from, to) {
    const path = this.findPath(from, to);
    return path !== null && path.length <= 2;
  }

  /**
   * Get the approximate path length
   * @param {Object} start - Start position {x, y, z}
   * @param {Object} end - End position {x, y, z}
   * @returns {number|null} Path length or null if no path
   */
  getPathLength(start, end) {
    const path = this.findPath(start, end);
    if (!path || path.length === 0) return null;

    let length = 0;
    let prev = start;

    for (const waypoint of path) {
      const dx = waypoint.x - prev.x;
      const dy = waypoint.y - prev.y;
      const dz = waypoint.z - prev.z;
      length += Math.sqrt(dx * dx + dy * dy + dz * dz);
      prev = waypoint;
    }

    return length;
  }
}

/**
 * Lightweight SurfaceGrid-compatible adapter for merged plane grids.
 * Provides the interface needed by GridPathfinder (isWalkable, getNeighbors,
 * worldToCell, cellToWorld, findNearestWalkableCell).
 */
class MergedPlaneGrid {
  constructor(cells, width, height, minX, minZ, cellSize, surfaceY) {
    this.cells = cells;
    this.width = width;
    this.height = height;
    this.minX = minX;
    this.minZ = minZ;
    this.maxX = minX + width * cellSize;
    this.maxZ = minZ + height * cellSize;
    this.cellSize = cellSize;
    this.surfaceY = surfaceY;
    this.neighborsCache = new Map();
  }

  isWalkable(cx, cz) {
    return this.inBounds(cx, cz) && this.cells[cx][cz];
  }

  inBounds(cx, cz) {
    return cx >= 0 && cx < this.width && cz >= 0 && cz < this.height;
  }

  worldToCell(worldX, worldZ) {
    const cellX = Math.floor((worldX - this.minX) / this.cellSize);
    const cellZ = Math.floor((worldZ - this.minZ) / this.cellSize);
    return {
      x: Math.max(0, Math.min(this.width - 1, cellX)),
      z: Math.max(0, Math.min(this.height - 1, cellZ))
    };
  }

  cellToWorld(cellX, cellZ) {
    return {
      x: this.minX + (cellX + 0.5) * this.cellSize,
      y: this.surfaceY,
      z: this.minZ + (cellZ + 0.5) * this.cellSize
    };
  }

  getNeighbors(cellX, cellZ) {
    const cacheKey = (cellX << 16) | cellZ;
    if (this.neighborsCache.has(cacheKey)) {
      return this.neighborsCache.get(cacheKey);
    }

    const neighbors = [];
    const SQRT2 = Math.SQRT2;
    const WALL_COST = 0.3;

    const directions = [
      { dx: -1, dz: 0, cost: 1 },
      { dx: 1, dz: 0, cost: 1 },
      { dx: 0, dz: -1, cost: 1 },
      { dx: 0, dz: 1, cost: 1 },
      { dx: -1, dz: -1, cost: SQRT2 },
      { dx: -1, dz: 1, cost: SQRT2 },
      { dx: 1, dz: -1, cost: SQRT2 },
      { dx: 1, dz: 1, cost: SQRT2 }
    ];

    for (const dir of directions) {
      const nx = cellX + dir.dx;
      const nz = cellZ + dir.dz;

      if (!this.isWalkable(nx, nz)) continue;

      // Diagonal: require both cardinal neighbors walkable (no corner cutting)
      if (dir.dx !== 0 && dir.dz !== 0) {
        if (!this.isWalkable(cellX + dir.dx, cellZ) ||
            !this.isWalkable(cellX, cellZ + dir.dz)) continue;
      }

      // Wall adjacency cost — prefer paths away from walls/obstacles
      let cost = dir.cost;
      let wallCount = 0;
      for (let ddx = -1; ddx <= 1; ddx++) {
        for (let ddz = -1; ddz <= 1; ddz++) {
          if (ddx === 0 && ddz === 0) continue;
          if (!this.isWalkable(nx + ddx, nz + ddz)) wallCount++;
        }
      }
      if (wallCount > 0) cost += WALL_COST * wallCount;

      neighbors.push({ x: nx, z: nz, cost });
    }

    this.neighborsCache.set(cacheKey, neighbors);
    return neighbors;
  }

  findNearestWalkableCell(worldX, worldZ) {
    const cell = this.worldToCell(worldX, worldZ);
    if (this.isWalkable(cell.x, cell.z)) return cell;

    // BFS to find nearest walkable cell
    const MAX_BFS = 500;
    const visited = new Set();
    const queue = [cell];
    visited.add(`${cell.x},${cell.z}`);

    let iter = 0;
    while (queue.length > 0 && ++iter <= MAX_BFS) {
      const current = queue.shift();
      for (let dx = -1; dx <= 1; dx++) {
        for (let dz = -1; dz <= 1; dz++) {
          if (dx === 0 && dz === 0) continue;
          const nx = current.x + dx;
          const nz = current.z + dz;
          const key = `${nx},${nz}`;
          if (visited.has(key) || !this.inBounds(nx, nz)) continue;
          visited.add(key);
          if (this.isWalkable(nx, nz)) return { x: nx, z: nz };
          queue.push({ x: nx, z: nz });
        }
      }
    }
    return null;
  }
}
